import { createClient } from '@supabase/supabase-js';
// .js extension required: this module is pulled in by node --test suites, which (unlike Vite)
// don't do extensionless resolution.
import {
  installAuthLandingGuard, getDeferredAuthLanding, clearDeferredAuthLanding,
  SIGN_IN_REQUEST_KEY, getPendingAnonMerge, clearPendingAnonMerge, clearSignInRequest, anonMergeDecision,
} from './authLandingGuard.js';
import { rateLimitMessage } from '../utils/rateLimitMessage.js';
import { isNetworkFailure } from '../utils/surfaceRule.js';
import { isLinkGroupSlug } from '../constants/audienceTokens.js';
import { readPreviewTab, previewClientOptions } from './previewTab.js';
import { isDeadSessionError } from './sessionCheck.js';
import { withHandoffCode, handoffLinkTarget } from './handoffUrl.js';

// Optional chaining so importing this module outside Vite (e.g. `node --test` unit suites that pull in
// the pure mapping/audience chain) doesn't throw on an undefined `import.meta.env` — Vite still inlines
// the values at build time, and a null client is already handled everywhere below.
const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

// Share/edit links: a ?share=<token> / ?edit=<token> in the landing URL is forwarded on every request
// as the x-share-token / x-edit-token header. RLS (token_grants_view / token_grants_edit) matches it
// against the row's share_token / edit_token, so possessing the link IS the grant — for anon sessions
// too. Read once at load (links are landing URLs); global headers ride all PostgREST + auth calls.
function shareLinkHeaders() {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const h = {};
  // The share token is also kept for the tab's session, so an app that rewrites its URL (the Climate
  // Week app does, on every tab change) still reads after a refresh that lost `?share=`. Session-scoped
  // on purpose: closing the tab forgets the grant, exactly as losing the link would.
  const stashed = (key) => { try { return window.sessionStorage?.getItem(key) || null; } catch { return null; } };
  const stash = (key, value) => { try { window.sessionStorage?.setItem(key, value); } catch { /* private mode, or storage off */ } };
  const s = p.get('share') || stashed('arcos.share'); if (s) { h['x-share-token'] = s; stash('arcos.share', s); }
  const e = p.get('edit'); if (e) h['x-edit-token'] = e;
  return h;
}

// Q5 — run BEFORE createClient. `detectSessionInUrl` below exchanges a magic link the instant the
// client is built, so this is the only point at which an existing sign-in can still be protected.
// When it defers, the auth params are stripped off the URL and stashed; the app prompts, and only an
// explicit "sign out & continue" applies them. See authLandingGuard.js.
// (Return value unused here — consumers ask via hasDeferredAuthLanding(), which reads the stash.)
if (typeof window !== 'undefined') installAuthLandingGuard(window, supabaseUrl);

// A preview tab (an admin seeing the space as one member, read-only) holds its own sign-in in this
// tab's sessionStorage and never touches the shared one — see previewTab.js. Null in every other tab.
export const PREVIEW_TAB = typeof window !== 'undefined' ? readPreviewTab(window) : null;

// Only create client if env vars are configured
const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, PREVIEW_TAB
    ? previewClientOptions(window, PREVIEW_TAB, supabaseUrl, shareLinkHeaders())
    : {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true, // process magic-link / OTP redirects on landing
      },
      global: { headers: shareLinkHeaders() },
    })
  : null;

// A sign-in LINK that this browser asked for, landing over the anonymous session that asked, left a
// pending merge (authLandingGuard.js). Once the session is the real account, fold the anonymous one
// into it — what verifyEmailSignIn already does for the typed code. Registered here, at load, so the
// SIGNED_IN that detectSessionInUrl fires is never missed. The merge is deferred out of the callback:
// supabase-js holds its auth lock while it runs, and a request awaited inside it can deadlock.
if (supabase && typeof window !== 'undefined' && !PREVIEW_TAB) {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event !== 'SIGNED_IN' && event !== 'INITIAL_SESSION') return;
    const pending = getPendingAnonMerge(window);
    const decision = anonMergeDecision(pending, session?.user);
    if (decision === 'none' || decision === 'wait') return;
    clearPendingAnonMerge(window);
    clearSignInRequest(window);
    if (decision === 'merge') setTimeout(() => { mergeAnonymousAccount(pending.from); }, 0);
  });
}

/**
 * Upload a file to Supabase Storage.
 * @param {string} bucket - Storage bucket name
 * @param {string} path - File path within bucket (e.g. "workspace/recordId/filename.jpg")
 * @param {File} file - File object to upload
 * @returns {Promise<string>} Public URL of uploaded file
 */
export async function uploadFile(bucket, path, file) {
  if (!supabase) throw new Error('Supabase not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');

  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { upsert: true });

  if (error) throw new Error(`Upload failed: ${error.message}`);

  return getPublicUrl(bucket, path);
}

/**
 * Get the public URL for a file in Supabase Storage.
 */
export function getPublicUrl(bucket, path) {
  if (!supabase) return null;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl || null;
}

// ───────────────────────────────────────────────────────────────────────────
// Auth — Phase A identity wedge (UNENFORCED: identity lives in Supabase, entity
// data stays on Airtable). The accounts row is created/maintained by the
// handle_new_user / handle_auth_user_claim DB triggers — never written from here.
// See atlasdbcanonicalschema §4/§14 and auth-privacy spec §3.0/§3.2.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Ensure a Supabase session exists. If none, mint an anonymous Temp User
 * (the handle_new_user trigger creates the matching accounts row, claimed_at NULL).
 * Idempotent: concurrent/repeat calls share one in-flight sign-in.
 * @returns {Promise<import('@supabase/supabase-js').Session | null>}
 */
// Dev-only off-switch for the seeded-admin auto-login below. Because signOut()
// re-mints a session immediately, and this file otherwise always prefers the dev
// admin, "Sign out" in local dev just loops you back in. Setting this flag lets an
// explicit dev sign-out stick (fall through to an anonymous Guest) until it's
// cleared. Persisted in localStorage so it survives reloads + new tabs in the same
// browser. No effect in production (import.meta.env.DEV is false).
const DEV_LOGIN_OFF_KEY = 'dev-auto-login-off';
export function isDevAutoLoginDisabled() {
  if (!import.meta.env.DEV) return false;
  try { return localStorage.getItem(DEV_LOGIN_OFF_KEY) === '1'; } catch { return false; }
}
export function setDevAutoLoginDisabled(off) {
  try {
    if (off) localStorage.setItem(DEV_LOGIN_OFF_KEY, '1');
    else localStorage.removeItem(DEV_LOGIN_OFF_KEY);
  } catch { /* ignore */ }
}

/**
 * DEV only: let the seeded-admin auto-login below finish before the app renders.
 *
 * The router fires ensureAnonymousSession() without awaiting it, and a dozen reads start on mount
 * (the space's id and door check, its config, the graph, the chat hooks). On a tab with no stored
 * session they all went out as the anonymous role and RLS answered them for nobody: a fresh browser
 * on `npm run dev:local` opened a space empty — no chats, the generic bottom bar — and only a reload
 * (session now in storage) showed it. Found 2026-10-02 on the local Personal Space copy. The event
 * page had already fixed the same race for itself (useEventData); this fixes it for every route.
 *
 * Waits at most `timeoutMs`, so a stalled local auth server cannot hold the page (index.html's
 * splash dead-man is twenty seconds). A no-op outside DEV, when no dev admin is configured, and
 * after an explicit dev sign-out, so production and the signed-out dev test are untouched.
 */
export async function settleDevSignIn(timeoutMs = 6000) {
  if (!import.meta.env.DEV || !supabase || !import.meta.env.VITE_DEV_ADMIN_EMAIL || isDevAutoLoginDisabled()) return;
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  try {
    await Promise.race([ensureAnonymousSession().catch(() => null), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

let _anonInFlight = null;
let _devAdminInFlight = null;
export async function ensureAnonymousSession() {
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  // A preview tab is the previewed person or nobody. It never signs in as anyone else — not the dev
  // admin, not an anonymous visitor — so an ended preview stays visibly ended.
  if (PREVIEW_TAB) return session;
  // Keep any real (claimed) session as-is.
  if (session && !session.user?.is_anonymous) return session;

  // Dev-only: prefer a seeded admin account so local previews get genuine,
  // RLS-passing write access instead of an anonymous viewer. Gated on
  // import.meta.env.DEV so it NEVER runs in a production build. Credentials live
  // in .env.local (VITE_DEV_ADMIN_EMAIL / VITE_DEV_ADMIN_PASSWORD) and are
  // copied into each worktree, so new local hosts auto-login with no input.
  // Skipped when the dev off-switch is set (an explicit dev sign-out) so you can
  // test the signed-out / Guest state on the same server.
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_ADMIN_EMAIL && !isDevAutoLoginDisabled()) {
    if (!_devAdminInFlight) {
      _devAdminInFlight = supabase.auth
        .signInWithPassword({
          email: import.meta.env.VITE_DEV_ADMIN_EMAIL,
          password: import.meta.env.VITE_DEV_ADMIN_PASSWORD,
        })
        .then(({ data, error }) => {
          if (error) { console.warn('[auth] dev admin sign-in failed:', error.message); return null; }
          return data.session;
        })
        .catch((e) => { console.warn('[auth] dev admin sign-in error:', e); return null; })
        .finally(() => { _devAdminInFlight = null; });
    }
    const devSession = await _devAdminInFlight;
    if (devSession) return devSession;
    // dev sign-in failed — fall through to the anonymous path below
  }

  if (session) return session;
  if (!_anonInFlight) {
    _anonInFlight = supabase.auth
      .signInAnonymously()
      .then(({ data, error }) => {
        if (error) {
          console.warn('[auth] anonymous sign-in failed:', error.message);
          return null;
        }
        return data.session;
      })
      .catch((e) => {
        console.warn('[auth] anonymous sign-in error:', e);
        return null;
      })
      .finally(() => {
        _anonInFlight = null;
      });
  }
  return _anonInFlight;
}

/** The current session, or null. */
export async function getSession() {
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Current account id (= accounts.id = auth.uid()), or null when signed out / unconfigured.
 *
 * Answered from a cached copy, because a page load asks this a dozen times over and the answer
 * cannot change between two of them. Both ways of asking gotrue cost more than they look:
 * `getSession()` takes its single exclusive Web Lock, and `getUser()` takes that lock AND asks the
 * server. Once the queue on that lock passes auth-js's five-second `lockAcquireTimeout`, the waiter
 * STEALS the lock and the holder's promise rejects with `AbortError: Lock was stolen by another
 * request` — unhandled, because nothing in the chain expects reading an id to fail. That is the
 * console full of AbortErrors on the Climate Week programme: eleven `/auth/v1/user` requests in one
 * load (measured 2026-09-18), every one of them for an id the session already held.
 *
 * The listener is what keeps it true rather than merely fast: gotrue fires INITIAL_SESSION on
 * subscribe and again on every sign-in, sign-out and token refresh, so the copy turns over whenever
 * the identity does.
 *
 * This answers which account the browser is ACTING AS, which is what query filters and
 * `created_by` / `updated_by` stamps need — and RLS re-decides every one of those server-side.
 * Anything that needs the SERVER's opinion of the session, such as noticing a token revoked on
 * another device, must still call `getSession()` or `supabase.auth.getUser()` for itself.
 */
let _accountId;                  // undefined = not resolved yet; null = signed out
let _accountIdInFlight = null;
if (supabase) supabase.auth.onAuthStateChange((_event, session) => { _accountId = session?.user?.id ?? null; });
export async function getCurrentAccountId() {
  if (_accountId !== undefined) return _accountId;
  if (!supabase) return null;
  // One read, however many callers arrive before it lands.
  if (!_accountIdInFlight) {
    _accountIdInFlight = getSession()
      .then((s) => {
        // The listener wins. A sign-in that lands while this read is in the air has the newer
        // answer, and letting the older one settle on top of it would leave the app acting as
        // nobody for the rest of the page's life — exactly the window anonymous sign-in opens.
        if (_accountId === undefined) _accountId = s?.user?.id ?? null;
        return _accountId;
      })
      .finally(() => { _accountIdInFlight = null; });
  }
  return _accountIdInFlight;
}

/** The cached account id without waiting: undefined until it has resolved once, null when signed out. */
export function peekCurrentAccountId() {
  return _accountId;
}

/**
 * Both credentials this browser holds for a space: the legacy edit-key (Airtable spaces / anon-guest
 * bootstrap) when one is in localStorage, AND the signed-in Supabase access token (RLS spaces, where
 * the edit-key is retired). Servers accept whichever validates, so the same call works on both
 * backends — and the same pair proves membership for a READ as proves admin for a write; only the
 * height of the server's gate differs. Async — it reads the live session.
 * @param {string} slug
 * @returns {Promise<Record<string,string>>}
 */
/**
 * Auth headers for an ACCOUNT-level route (/api/connect/*) — the bearer token and nothing else.
 *
 * Distinct from spaceAuthHeaders on purpose: these endpoints are not space-scoped, so a space's
 * edit key is not merely unnecessary, it is the wrong credential to be sending. An account-owned
 * credential must never be reachable by possession of one space's key.
 */
export async function accountAuthHeaders() {
  const session = await getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

/**
 * Open a read-only preview of the space as one member (admins and owners only). Asks the server for
 * the preview tab's token; the caller hands it to the new tab (src/api/previewTab.js).
 * @returns {Promise<{ token, expiresAt, previewId, person: { id, displayName, email } }>}
 * @throws {Error} with a sentence a person can read.
 */
export async function openPreviewAs(spaceSlug, accountId) {
  const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/preview-as`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await accountAuthHeaders()) },
    body: JSON.stringify({ accountId }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok && body?.token) return body;
  const why = {
    'admins-only': 'Only admins and owners of this space can preview a member.',
    'not-a-member': 'That person is not an active member of this space.',
    'cannot-preview-yourself': 'That is you — open the space in this tab to see it as yourself.',
    'already-previewing': 'A preview tab cannot open another preview.',
    'preview-not-configured': 'Previews are not set up on this server yet.',
  }[body?.error];
  throw new Error(why || 'The preview could not be opened. Try again in a moment.');
}

/**
 * The space's demo member — the account "View as → Member" previews as — made the first time an
 * admin asks (api/workspace/[slug]/demo-member.js). Preview it with openPreviewAs(slug, accountId).
 * @returns {Promise<{ accountId: string, profileItemId: string, displayName: string }>}
 */
export async function ensureDemoMember(spaceSlug) {
  const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/demo-member`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await accountAuthHeaders()) },
    body: '{}',
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok && body?.accountId) return body;
  const why = {
    'admins-only': 'Only admins and owners of this space can preview as a member.',
    'already-previewing': 'A preview tab cannot open another preview.',
  }[body?.error];
  throw new Error(why || 'The sample member could not be set up. Try again in a moment.');
}

export async function spaceAuthHeaders(slug) {
  const headers = {};
  try {
    const k = (typeof window !== 'undefined' && slug) ? localStorage.getItem(`editKey:${slug}`) : null;
    if (k) headers['x-edit-key'] = k;
  } catch { /* localStorage unavailable — fall through to the bearer token */ }
  const session = await getSession();
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

/**
 * Auth headers for a workspace-config WRITE (PATCH /api/workspace/<slug>, resync, …). The same
 * credential pair as spaceAuthHeaders; the name records the intent at the ~40 call sites that use it,
 * where the server gate (api/_shared/workspaceAdminAuth.js) is admin/owner.
 * @param {string} slug
 * @returns {Promise<Record<string,string>>}
 */
export const workspaceWriteHeaders = spaceAuthHeaders;

/**
 * Collapse concurrent identical reads onto one request.
 *
 * Only while the read is IN FLIGHT: the entry is dropped the moment it settles, so nothing is ever
 * served out of a stale cache — the most a caller can get is an answer that was already on its way
 * when it asked. That is what makes this safe to put in front of a membership check, where a cache
 * with any lifetime at all would be a way to keep reading a space you have just been removed from.
 *
 * It is here because a space boot asks the same questions from several hooks in the same tick:
 * `useAccess`, `EditContext` and the programme's own `useMembership` each call
 * `fetchMyGroupSlugs`, and two surfaces each call `fetchMyProfileItemId`. Measured on the Climate
 * Week programme (2026-09-18): six near-identical `group_members` reads, all starting within 1 ms
 * of each other, none of which could have disagreed.
 */
const _sharedReads = new Map();
function sharedRead(key, run) {
  const existing = _sharedReads.get(key);
  if (existing) return existing;
  const p = run().finally(() => { _sharedReads.delete(key); });
  _sharedReads.set(key, p);
  return p;
}

/**
 * The current account's ACTIVE group slugs in a space (e.g. ['admins'] / ['members']) — the
 * membership input to the access resolver. RLS scopes group_members to the caller's own rows,
 * so this returns just *your* memberships; the !inner embeds filter to the named space.
 * @returns {Promise<string[]>}
 */
/**
 * Thread C / D-1 — MY membership status in a space, including the non-active states that
 * `fetchMyGroupSlugs` filters out. Needed once passive auto-join is gone: a knock-door space has to
 * tell "you haven't asked yet" (offer Request to join) apart from "you asked, an admin hasn't
 * decided" (say so, no button) — previously indistinguishable, because the silent auto-join filed
 * the application for you. Self-scoped, so the existing self-read RLS covers it; no RPC needed.
 * @returns {Promise<'active'|'applied'|'invited'|'suspended'|'left'|'removed'|null>} null = no row
 */
export async function fetchMySpaceMembershipStatus(spaceSlug, accountId = null) {
  if (!supabase || !spaceSlug) return null;
  // `accountId` from the caller when it has one; otherwise the cached id, which costs nothing.
  const uid = accountId || await getCurrentAccountId();
  if (!uid) return null;
  return sharedRead(`membershipStatus|${uid}|${spaceSlug}`, async () => {
    const { data, error } = await supabase
      .from('group_members')
      .select('status, spaces!inner(slug)')
      .eq('account_id', uid)
      .eq('spaces.slug', spaceSlug);
    if (error) { console.warn('[access] membership status read failed:', error.message); return null; }
    if (!data?.length) return null;
    // Several rows can exist (members + admins); report the most-established one.
    const rank = { active: 4, invited: 3, applied: 2, suspended: 1 };
    return data.map((r) => r.status).sort((a, b) => (rank[b] || 0) - (rank[a] || 0))[0] || null;
  });
}

/**
 * Every group the signed-in account is active in, in EVERY space — ids only. The client's view
 * checks resolve `@group:<id>` (and `@personal-group:<id>`) tokens against this, mirroring
 * group_token_match / personal_group_match. `gm_self_read` lets anyone read their own rows.
 * @returns {Promise<string[]>}
 */
export async function fetchMyGroupIds(accountId = null) {
  if (!supabase) return [];
  const uid = accountId || await getCurrentAccountId();
  if (!uid) return [];
  return sharedRead(`groupIds|${uid}`, async () => {
    const { data, error } = await supabase.from('group_members')
      .select('group_id').eq('account_id', uid).eq('status', 'active');
    if (error) { console.warn('[groups] fetchMyGroupIds failed:', error.message); return []; }
    return (data || []).map((r) => r.group_id);
  });
}

/**
 * The groups of every space I am a member of — what the audience pickers may offer as
 * `@group:<id>`. Personal Spaces are excluded (their groups are `@personal-group:`).
 * @returns {Promise<Array<{id,slug,name,spaceId,spaceSlug,spaceName,memberCount}>>}
 */
export async function fetchMySpaceGroups() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('my_space_groups');
  if (error) { console.warn('[groups] my_space_groups failed:', error.message); return []; }
  return (data || []).map((r) => ({
    id: r.r_group_id, slug: r.r_slug, name: r.r_display_name || r.r_slug,
    spaceId: r.r_space_id, spaceSlug: r.r_space_slug, spaceName: r.r_space_name,
    memberCount: Number(r.r_member_count ?? 0),
  }));
}

/**
 * Labels for `@group:<id>` tokens. A member of the group's space gets its name, space and size;
 * anyone else gets `name: null`, which `spaceGroupLabel` reads as "A group in another space".
 * @returns {Promise<Object<string, {name, spaceName, memberCount, isMine}>>}
 */
export async function describeSpaceGroups(groupIds) {
  const ids = [...new Set((groupIds || []).filter(Boolean))];
  if (!supabase || !ids.length) return {};
  const { data, error } = await supabase.rpc('describe_space_groups', { p_group_ids: ids });
  if (error) { console.warn('[groups] describe_space_groups failed:', error.message); return {}; }
  const out = {};
  for (const r of data || []) {
    out[r.r_group_id] = {
      name: r.r_display_name || null, spaceName: r.r_space_name || null,
      memberCount: r.r_member_count == null ? null : Number(r.r_member_count), isMine: !!r.r_is_mine,
    };
  }
  return out;
}

export async function fetchMyGroupSlugs(spaceSlug, accountId = null) {
  if (!supabase || !spaceSlug) return [];
  // Our own uid — handed in by a caller that holds one, else the cached copy. This used to call
  // `getSession()`, and a space boot took gotrue's auth lock four or five times over for an id
  // React already had in hand: once here, once in the membership-status read beside it, once in
  // spaceAuthHeaders, once in EditContext's own copy of this call, once more on /personal to
  // resolve the slug. `getCurrentAccountId` answers from cache now, so the fallback costs nothing
  // and the contention that list described — mobile Safari logging "Lock was not released within
  // 5000ms. Forcefully acquiring the lock to recover", every waiter behind it stalling — no longer
  // begins here.
  const uid = accountId || await getCurrentAccountId();
  if (!uid) return [];
  // One request, however many hooks ask in the same tick. See sharedRead.
  return sharedRead(`groupSlugs|${uid}|${spaceSlug}`, () => readMyGroupSlugs(spaceSlug, uid));
}

/**
 * My groups in a space, for the account menu's space card: each group's slug, name and whether it is
 * one of the role slots, with when I joined it. Same scoping as fetchMyGroupSlugs: my rows only,
 * active only (RLS would otherwise hand back the whole roster). Archived groups are left out.
 * @returns {Promise<Array<{ slug: string, name: string, isSystem: boolean, joinedAt: string|null }>>}
 */
export async function fetchMyGroupsInSpace(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const uid = await getCurrentAccountId();
  if (!uid) return [];
  const { data, error } = await supabase
    .from('group_members')
    .select('joined_at, groups!inner(slug, display_name, is_system, lifecycle), spaces!inner(slug)')
    .eq('status', 'active')
    .eq('account_id', uid)
    .eq('spaces.slug', spaceSlug);
  if (error) throw new Error(error.message);
  return (data || [])
    .filter((r) => r.groups && r.groups.lifecycle !== 'archived')
    .map((r) => ({ slug: r.groups.slug, name: r.groups.display_name, isSystem: !!r.groups.is_system, joinedAt: r.joined_at || null }));
}

/**
 * A space's groups as the account menu's Groups view lists them, with my standing in each: every
 * non-role group open to requests or shown, every one I am in or invited to, and for an admin every
 * group. This is the server's `applicable_groups`, which the programme's Groups page reads too —
 * SECURITY DEFINER, because a direct read of `groups` returns nothing to a non-member.
 * @returns {Promise<Array<{ slug, display_name, description, my_status, join_policy, roster_visibility, member_count }>>}
 */
export async function fetchSpaceGroupsForMe(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('applicable_groups', { p_space_slug: spaceSlug });
  if (error) throw new Error(error.message);
  return data || [];
}

/** Ask to join a group, or join it at once when it is open. Returns `apply_to_group`'s word. */
export async function applyToGroup(spaceSlug, groupSlug) {
  if (!supabase) return 'error';
  const { data, error } = await supabase.rpc('apply_to_group', { p_space_slug: spaceSlug, p_group_slug: groupSlug });
  if (error) { console.warn('[groups] apply_to_group failed:', error.message); return 'error'; }
  return data;
}

async function readMyGroupSlugs(spaceSlug, uid) {
  const { data, error } = await supabase
    .from('group_members')
    .select('groups!inner(slug), spaces!inner(slug)')
    .eq('status', 'active')
    .eq('account_id', uid)   // MY memberships only — RLS permits reading the space roster, so scope explicitly or every member reads the admins row too
    .eq('spaces.slug', spaceSlug);
  // A query FAILURE is not an answer. Returning [] here used to make "the network blinked" and
  // "you hold no role in this space" the same value, and the surface rule reads that value as the
  // second one: on a HIDDEN space a non-member gets O1, so one failed read turned a member's own
  // space into a permanent "This page isn't available" offering to re-check their invitation.
  // /personal is hidden by construction (ensure_personal_space sets door_discovery='hidden'), so
  // every account's own home sat one dropped request away from that screen. Throw instead, exactly
  // as resolveItemSpaceSlug does and for the same reason — the caller can then retry and, failing
  // that, say it couldn't check rather than deliver a verdict it never reached.
  if (error) {
    console.warn('[access] fetchMyGroupSlugs failed:', error.code || '', error.message);
    // The CODE travels with the message: "your sign-in expired" and "the network dropped" need
    // different words and different buttons, and only the code tells them apart reliably.
    const e = new Error(error.message);
    e.code = error.code || null;
    throw e;
  }
  return (data || []).map((r) => r.groups?.slug).filter(Boolean);
}

/**
 * Open-door auto-join: become a member (door='open') / pending applicant (door='knock') of a space
 * by slug. Resolved server-side by the join_space_by_slug SECURITY DEFINER function — a non-member
 * can't read the spaces row to resolve the id, so the RPC does it outside RLS. Idempotent.
 * @returns {Promise<string|null>} resulting status ('active'|'applied'|'locked'|'no-space'|...).
 */
export async function joinSpaceBySlug(spaceSlug) {
  if (!supabase || !spaceSlug) return null;
  const { data, error } = await supabase.rpc('join_space_by_slug', { p_slug: spaceSlug });
  if (error) { console.warn('[access] joinSpaceBySlug failed:', error.message); return null; }
  return data;
}

/** Subscribe to auth state changes; returns an unsubscribe fn. */
export function onAuthStateChange(callback) {
  if (!supabase) return () => {};
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) =>
    callback(event, session),
  );
  return () => subscription.unsubscribe();
}

/**
 * Start an email claim on the current (anonymous) session. GoTrue emails a
 * confirmation (magic link + 6-digit code); on confirm it re-binds the SAME user
 * UUID (is_anonymous → false), and handle_auth_user_claim stamps accounts.claimed_at.
 * Phase-A thin re-bind — the claim_intents return_to/side_effect engine is the fast-follow.
 */
export async function startEmailClaim(email, { redirectTo } = {}) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.auth.updateUser(
    { email },
    { emailRedirectTo: redirectTo ?? `${window.location.origin}${window.location.pathname}${window.location.search}` },
  );
  if (error) throw new Error(`Claim start failed: ${error.message}`);
  const user = data?.user ?? null;
  // Local dev auto-confirms email changes (no email sent) → claim completes instantly.
  // Staging/prod with confirmations ON returns unconfirmed → caller shows the OTP code step.
  const confirmed = !!(user && user.email === email && user.email_confirmed_at);
  return { user, confirmed };
}

/** Verify an emailed OTP code to complete the claim (the cross-device-safe alternative to the link). */
export async function verifyEmailClaim(email, token) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email_change' });
  if (error) throw new Error(`Claim verification failed: ${error.message}`);
  return data;
}

/**
 * Confirm ONE inbox's code during a change of email address (Account settings), and say whether
 * that finished the change.
 *
 * With secure email change on — production, where confirmations are on — the current address and
 * the new one each get their own code, and both must be confirmed. The first comes back with no
 * session (GoTrue's "Confirmation link accepted. Please proceed to confirm link sent to the other
 * email"), the second with the session for the new address. With confirmations off, as on the local
 * stack, either one finishes it. So the answer is read from what came back, never assumed. Each code
 * is checked against the address its own email went to: the current inbox's code with the current
 * address, the new inbox's with the new one.
 *
 * @returns {Promise<{ complete: boolean }>}
 */
export async function verifyEmailChangeCode(address, code) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.auth.verifyOtp({ email: address, token: code, type: 'email_change' });
  if (error) throw new Error(error.message);
  return { complete: !!data?.session };
}

/**
 * Start a sign-in to an EXISTING account by email (OTP + magic link). Use when the email is
 * already claimed (the claim path 422s "already registered"). On sign-in the session switches to
 * that account, and the current anonymous account is merged into it — by verifyEmailSignIn for the
 * typed code, and by the landing listener above for the link. `shouldCreateUser:false` so a typo
 * can't silently mint a brand-new account here — first-time saves go through the claim path instead.
 *
 * Records which anonymous account asked, and for which address (SIGN_IN_REQUEST_KEY): that record is
 * what lets the link merge, and what stops a link this browser never asked for from merging anything.
 */
export async function startEmailSignIn(email, { redirectTo } = {}) {
  if (!supabase) throw new Error('Supabase not configured');
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.is_anonymous) {
      window.localStorage.setItem(SIGN_IN_REQUEST_KEY, JSON.stringify({ anon: session.user.id, email, at: Date.now() }));
    } else {
      clearSignInRequest(window);
    }
  } catch { /* storage off: the code path still merges, the link will not */ }
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false, emailRedirectTo: redirectTo ?? `${window.location.origin}${window.location.pathname}${window.location.search}` },
  });
  if (error) throw new Error(error.message);
}

/**
 * Verify the OTP for an existing-account sign-in (type 'email', vs 'email_change' for a claim).
 *
 * C6 account merge: signing in to an EXISTING account switches the session to that account's *different*
 * UUID, orphaning everything the current anonymous session created/owns. So BEFORE verifyOtp switches us,
 * capture the prior session's UUID iff it was anonymous; AFTER a successful switch to a different account,
 * fold the anon's ownership/grants into the now-signed-in account via merge_account (server-side, RLS-safe).
 * Best-effort: a merge failure is warned, not thrown — the user is still signed in. (The CLAIM path
 * re-binds the SAME UUID, so it needs no merge.) The magic-link path lands via detectSessionInUrl, not
 * here; it merges through the landing listener near the top of this file (2026-10-08).
 */
export async function verifyEmailSignIn(email, token) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data: { session: priorSession } } = await supabase.auth.getSession();
  const priorAnonId = priorSession?.user?.is_anonymous ? priorSession.user.id : null;

  const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
  if (error) throw new Error(`Sign-in verification failed: ${error.message}`);

  const newId = data?.user?.id ?? null;
  // The code did the sign-in, so nothing is left for a link to do.
  clearSignInRequest(window);
  clearPendingAnonMerge(window);
  if (priorAnonId && newId && priorAnonId !== newId) await mergeAnonymousAccount(priorAnonId);
  return data;
}

// ── A phone to sign in with ──────────────────────────────────────────────────────────────────────
// Plan: vault "Phone Numbers for Sign-In and Profile Claiming — Phase One Build Plan". A member adds a
// phone to an account they already have, confirmed by a texted code, and can then sign in by text.
// A phone never creates an account: the app asks Supabase not to (`shouldCreateUser: false`), and
// the `hook_before_user_created` hook (migration 20261126090000) refuses it on the server. Production
// sends through Twilio Verify; the local stack uses fixed test numbers (supabase/config.toml).
// Errors are thrown as Supabase returns them, `code` and all — signInPhone.js `phoneCodeMessage` turns
// them into words.

/** Text a code to add (or change) the phone on the signed-in account. Nothing changes until it's confirmed. */
export async function startAddPhone(phone) {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.auth.updateUser({ phone });
  if (error) throw error;
}

/** Confirm the code texted by startAddPhone. The number is the account's sign-in phone after this. */
export async function verifyAddPhone(phone, token) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.auth.verifyOtp({ phone, token, type: 'phone_change' });
  if (error) throw error;
  return data;
}

/** Remove the signed-in account's phone; signing in is by email alone afterwards. */
export async function removeSignInPhone() {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.rpc('remove_my_sign_in_phone');
  if (error) throw error;
  // The session still carries the old number until it is read again.
  await supabase.auth.refreshSession().catch(() => {});
}

/**
 * Confirm the signed-in account's own email with a code typed in the app (api/me/confirm-email.js):
 * `send` emails a code, `check` checks one (`{ code }`), `change` puts the right address on the account
 * and sends its code (`{ email }`). Resolves to the route's `{ ok, error? }`; after a successful check
 * the session is read again, so the app sees the address as confirmed at once.
 */
export async function confirmMyEmail(action, payload = {}) {
  if (!supabase) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { ok: false, error: 'not-signed-in' };
  try {
    const res = await fetch('/api/me/confirm-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action, ...payload }),
    });
    const out = await res.json().catch(() => ({ ok: false, error: 'bad-response' }));
    if (out.ok && action !== 'send') await supabase.auth.refreshSession().catch(() => {});
    return out;
  } catch {
    return { ok: false, error: 'network' };
  }
}

/** Text a sign-in code to an EXISTING account's phone. A number on no account is refused, never signed up. */
export async function startPhoneSignIn(phone) {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.auth.signInWithOtp({ phone, options: { shouldCreateUser: false } });
  if (error) throw error;
}

/**
 * Verify a texted sign-in code. Like verifyEmailSignIn: the session switches to the phone's account,
 * and the anonymous account this browser was using is folded into it, so what was done signed out
 * is kept. There is no link to land by, so the code is the only path and the merge is always here.
 */
export async function verifyPhoneSignIn(phone, token) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data: { session: priorSession } } = await supabase.auth.getSession();
  const priorAnonId = priorSession?.user?.is_anonymous ? priorSession.user.id : null;

  const { data, error } = await supabase.auth.verifyOtp({ phone, token, type: 'sms' });
  if (error) throw error;

  const newId = data?.user?.id ?? null;
  clearPendingAnonMerge(window);
  if (priorAnonId && newId && priorAnonId !== newId) await mergeAnonymousAccount(priorAnonId);
  return data;
}

/**
 * Phones this person has already given, for "Use … from your profile": the `tel:` links on their Home
 * contacts and their own Phone answers on community profiles. Only cells they wrote, which RLS lets
 * them read. Best effort: a failed read offers nothing rather than an error.
 *
 * @returns {Promise<{ homeLinks: string[], answers: string[] }>}
 */
export async function loadMyPhoneNumbers(accountId) {
  const none = { homeLinks: [], answers: [] };
  if (!supabase || !accountId) return none;
  try {
    const { data: fields } = await supabase.from('fields').select('id, key')
      .is('primary_space_id', null).in('key', ['profile_link', 'phone']);
    const idOf = (key) => (fields || []).find((f) => f.key === key)?.id || null;
    const ids = [idOf('profile_link'), idOf('phone')].filter(Boolean);
    if (!ids.length) return none;
    const { data: cells } = await supabase.from('cells').select('field_id, value_text, value_json')
      .eq('author_id', accountId).in('field_id', ids).limit(200);
    const out = { homeLinks: [], answers: [] };
    for (const c of cells || []) {
      if (c.field_id === idOf('profile_link')) {
        const url = c.value_json?.url || '';
        if (/^tel:/i.test(url)) out.homeLinks.push(url);
      } else if (c.value_text) {
        out.answers.push(c.value_text);
      }
    }
    return out;
  } catch {
    return none;
  }
}

/**
 * Fold an anonymous account into the signed-in caller (merge_account, server-side). merge_account
 * refuses anything but an anonymous, unclaimed account and is a no-op for one already merged, so a
 * repeat is harmless. Best-effort: a failure is warned, never thrown — the person is signed in.
 */
async function mergeAnonymousAccount(fromId) {
  if (!supabase || !fromId) return null;
  try {
    const { data: status, error } = await supabase.rpc('merge_account', { p_from: fromId });
    if (error) { console.warn('[auth] merge_account failed:', error.message); return null; }
    if (status !== 'merged') console.warn('[auth] merge_account did not merge:', status);
    return status;
  } catch (e) {
    console.warn('[auth] merge_account error:', e);
    return null;
  }
}

/**
 * Arrive signed in from another Arcos address (the hand-off: api/_handoff.js, api/auth/handoff.js).
 *
 * A sign-in already here is never replaced — not by the same account, not by another — so a link can
 * never quietly change who someone is signed in as; the unused code simply expires. An anonymous
 * session is replaced, and what it did is folded into the account the way verifyEmailSignIn does it.
 *
 * @returns {Promise<'signed-in'|'kept'|'skipped'|string>} what happened; a string error otherwise
 */
export async function signInFromHandoff(code) {
  if (!supabase || !code || PREVIEW_TAB) return 'skipped';
  const { data: { session } } = await supabase.auth.getSession();
  if (session && !session.user?.is_anonymous) return 'kept';
  const priorAnonId = session?.user?.is_anonymous ? session.user.id : null;
  try {
    const res = await fetch('/api/auth/handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'swap', code }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.access_token) return body.error || `HTTP ${res.status}`;
    const { data, error } = await supabase.auth.setSession({ access_token: body.access_token, refresh_token: body.refresh_token });
    if (error) return error.message;
    const newId = data?.user?.id ?? null;
    if (priorAnonId && newId && priorAnonId !== newId) await mergeAnonymousAccount(priorAnonId);
    return 'signed-in';
  } catch (err) {
    return err?.message || 'request-failed';
  }
}

/**
 * Go to another Arcos address, carrying this sign-in if there is one. Signed out, or if the code
 * cannot be had within a few seconds, the link simply opens: the hand-off is a convenience, never a
 * gate, and the address arrived at asks for a sign-in as it always did.
 */
export async function navigateAcross(href) {
  const go = (to) => window.location.assign(to);
  let target;
  try { target = new URL(href, window.location.href); } catch { return go(href); }
  if (target.origin === window.location.origin || !supabase || PREVIEW_TAB) return go(target.toString());
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || session.user?.is_anonymous) return go(target.toString());
    const res = await fetch('/api/auth/handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: 'mint', to: target.origin }),
      signal: AbortSignal.timeout(5000),
    });
    const body = await res.json().catch(() => ({}));
    return go(res.ok && body.code ? withHandoffCode(target.toString(), body.code) : target.toString());
  } catch {
    return go(target.toString());
  }
}

/**
 * Carry the sign-in on every plain click of a link to another Arcos address (handoffLinkTarget says
 * which). Capture phase, so it sees the click before anything stops it; it never stops it either,
 * so a sheet that closes on the same click still closes. Installed once, at boot.
 */
export function installHandoffLinks(win = typeof window !== 'undefined' ? window : null) {
  if (!win?.document || win.__arcosHandoffLinks) return;
  win.__arcosHandoffLinks = true;
  win.document.addEventListener('click', (event) => {
    const to = handoffLinkTarget(event, win.location.href);
    if (!to) return;
    event.preventDefault();
    navigateAcross(to);
  }, true);
}

/**
 * Sign out, then immediately re-mint an anonymous Temp User so the app stays usable.
 *
 * Only THIS browser's session ends (`scope: 'local'`). Supabase's default is 'global', which ends
 * every session the account has on every device — and the sign-in form signs out first whenever it
 * switches accounts, so signing in as someone else in one tab signed you out everywhere, leaving
 * other tabs half signed in (2026-10-08).
 */
export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut({ scope: 'local' });
  await ensureAnonymousSession();
}

/**
 * Asks the auth server whether this browser's session still exists, and signs out here if it does
 * not (src/api/sessionCheck.js says why a page cannot tell by itself). Reloads after, so every
 * cache keyed to the old account goes with it. Returns true when the session was dead.
 */
export async function checkSessionWithServer() {
  if (!supabase) return false;
  // A preview tab's session is one GoTrue has never had, on purpose (previewTab.js); the tab answers
  // "who am I" itself, and an ended preview is shown as ended rather than signed out.
  if (PREVIEW_TAB) return false;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return false;
  const { error } = await supabase.auth.getUser();
  if (!isDeadSessionError(error)) return false;
  console.warn('[auth] this session was ended elsewhere; signing out here:', error.code);
  await signOut();
  if (typeof window !== 'undefined') window.location.reload();
  return true;
}

/** Q5 — was this visit's magic link held back to protect an existing sign-in? (see authLandingGuard)
 *  R4-1 split the stash into two reasons; this keeps its original meaning (the wrong-account case)
 *  so WrongAccountPrompt doesn't fire over the consent gate. A legacy stash without a reason reads
 *  as wrong-account — the only kind that existed before. */
export function hasDeferredAuthLanding() {
  return deferredAuthLandingReason() === 'wrong-account';
}

/** R4-1 — why was this visit's magic link held back? 'wrong-account' | 'consent' | null. */
export function deferredAuthLandingReason() {
  if (typeof window === 'undefined') return null;
  const stash = getDeferredAuthLanding(window);
  if (!stash) return null;
  return stash.reason || 'wrong-account';
}

/**
 * Q5 — the explicit "sign out & continue as the invited account" action. Signs the current user out,
 * then applies the magic link we held back, so the deferral costs the invitee one click rather than
 * a dead link. Implicit-flow landings carry both tokens, so we can restore them directly; a PKCE
 * `code` can only be exchanged by the library, so we hand it back via the URL and reload.
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function applyDeferredAuthLanding({ holdForCaller = false } = {}) {
  if (!supabase || typeof window === 'undefined') return { ok: false, error: 'not-configured' };
  const stashed = getDeferredAuthLanding(window);
  if (!stashed) return { ok: false, error: 'nothing-deferred' };
  try {
    await supabase.auth.signOut({ scope: 'local' }); // this browser only — see signOut()
    if (stashed.kind === 'implicit') {
      const { error } = await supabase.auth.setSession({
        access_token: stashed.access_token, refresh_token: stashed.refresh_token,
      });
      if (error) return { ok: false, error: error.message };
      // holdForCaller (R4-1 consent gate): the caller has follow-up work to do in THIS page —
      // redeem the ?ct= deterministically — before reloading. Leave the stash in place too:
      // while it exists, WorkspaceRouter's redeem listener stands down, so the setSession
      // SIGNED_IN event can't race the caller's own redeem. The caller clears + reloads.
      if (holdForCaller) return { ok: true, kind: 'implicit' };
      clearDeferredAuthLanding(window);
      window.location.reload();
      return { ok: true, kind: 'implicit' };
    }
    // PKCE: put the code back on the URL and let detectSessionInUrl do the exchange on reload —
    // now safe, because we just signed out (the guard won't re-defer with no session persisted).
    // (No holdForCaller variant — the exchange NEEDS the reload; the ?ct= survives it now that
    // the URL cleaners preserve it, so the post-reload redeem effect picks it up normally.)
    clearDeferredAuthLanding(window);
    const url = new URL(window.location.href);
    url.searchParams.set('code', stashed.code);
    window.location.replace(url.toString());
    return { ok: true, kind: 'pkce' };
  } catch (err) {
    return { ok: false, error: err.message || 'failed' };
  }
}

/**
 * Set the signed-in account's display name (self-only, via the set_my_display_name RPC). Best-effort
 * for callers that ignore the result; the account menu reads `{ ok, error }` to say whether it saved.
 */
export async function setMyDisplayName(name) {
  if (!supabase || !name?.trim()) return { ok: false, error: 'No name given.' };
  const { error } = await supabase.rpc('set_my_display_name', { p_name: name.trim() });
  if (error) {
    console.warn('[setMyDisplayName] failed:', error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * The current account's own display name (or null if unset / signed out / unconfigured).
 * Resolves via the actor_labels RPC with the caller's own uuid — accounts is self-read-only,
 * so this is the column-safe path (name + avatar only, never email/phone). Used to label live
 * presence cursors (canvas + collaborative editor) with the real name instead of a guest handle.
 * @returns {Promise<string|null>}
 */
export async function getMyDisplayName() {
  if (!supabase) return null;
  const accountId = await getCurrentAccountId();
  if (!accountId) return null;
  const { data, error } = await supabase.rpc('actor_labels', { p_ids: [accountId] });
  if (error) { console.warn('[getMyDisplayName] actor_labels failed:', error.message); return null; }
  const row = (data || []).find((r) => r.id === accountId) || (data || [])[0];
  return row?.display_name?.trim() || null;
}

/**
 * The current account's own avatar URL (or null if unset). Reads via actor_labels (column-safe:
 * id + display_name + avatar_url only), same self-read path as getMyDisplayName.
 * @returns {Promise<string|null>}
 */
export async function getMyAvatarUrl() {
  if (!supabase) return null;
  const accountId = await getCurrentAccountId();
  if (!accountId) return null;
  const { data, error } = await supabase.rpc('actor_labels', { p_ids: [accountId] });
  if (error) { console.warn('[getMyAvatarUrl] actor_labels failed:', error.message); return null; }
  const row = (data || []).find((r) => r.id === accountId) || (data || [])[0];
  return row?.avatar_url || null;
}

/**
 * Batch name + avatar for arbitrary accounts, via the same email-free actor_labels RPC the note
 * surfaces use. Returns a map of account id → { displayName, avatarUrl }; unknown ids are simply
 * absent. This is the "Created by" resolver — the Info tab skipped its creator line for months
 * because a raw uuid had no name lookup here.
 * @param {string[]} accountIds
 * @returns {Promise<Record<string, { displayName: ?string, avatarUrl: ?string }>>}
 */
export async function fetchActorLabels(accountIds) {
  if (!supabase) return {};
  const ids = [...new Set((accountIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await supabase.rpc('actor_labels', { p_ids: ids });
  if (error) { console.warn('[actor-labels] fetch failed:', error.message); return {}; }
  return Object.fromEntries((data || []).map((r) => [r.id, {
    displayName: r.display_name?.trim() || null,
    avatarUrl: r.avatar_url || null,
  }]));
}

/**
 * Set (or clear) the signed-in account's avatar URL, self-only via the set_my_avatar_url RPC.
 * Pass null/'' to clear. Returns { ok } so the caller can surface a failure.
 */
export async function setMyAvatarUrl(url) {
  if (!supabase) return { ok: false, error: 'not-configured' };
  const { error } = await supabase.rpc('set_my_avatar_url', { p_url: url || null });
  if (error) { console.warn('[setMyAvatarUrl] failed:', error.message); return { ok: false, error: error.message }; }
  return { ok: true };
}

/**
 * Standalone item page (/item/<uuid>): resolve which space slug an item belongs to so the page can
 * boot the right workspace config without exposing the slug in the URL. Wraps the SECURITY DEFINER
 * space_slug_for_item RPC (returns ONLY the slug; RLS still gates whether the item itself is visible).
 * @returns {Promise<string|null>} the space slug, or null if the item doesn't exist / is deleted.
 */
export async function resolveItemSpaceSlug(itemId) {
  if (!supabase || !itemId) return null;
  const { data, error } = await supabase.rpc('space_slug_for_item', { p_item: itemId });
  // Distinguish a genuine miss (data null → item doesn't exist → O1) from an RPC/network failure
  // (throw → the caller shows a retryable error, NOT a permanent "not available" 404). A missing
  // function on an un-migrated prod is an RPC error, so it surfaces as retryable, not a hard 404.
  if (error) { console.warn('[resolveItemSpaceSlug] failed:', error.message); throw new Error(error.message); }
  return data || null;
}

// Resolve the current (claimed) user's Personal space slug, so the /personal
// route can boot it without the (id-derived) slug ever being in the URL.
// Returns null when signed out / anonymous / no personal space yet.
export async function resolvePersonalSpaceSlug(accountId = null) {
  if (!supabase) return null;
  // The caller's own id when it has one — same lock-contention reason as fetchMyGroupSlugs, and
  // this one sits on the very first step of the /personal boot, where nothing has loaded yet to
  // hide the wait behind.
  let uid = accountId;
  if (!uid) {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user || user.is_anonymous) return null;
    uid = user.id;
  }
  // Same rule as resolveItemSpaceSlug and fetchMyGroupSlugs: a failed read throws, so the router
  // shows a RETRYABLE error; only a genuine "no personal space yet" returns null → not-found.
  // Folding the error into null sent a transient blip to the permanent not-found screen.
  const { data: acct, error: aErr } = await supabase
    .from('accounts').select('personal_space_id').eq('id', uid).maybeSingle();
  if (aErr) { console.warn('[personal] account read failed:', aErr.message); throw new Error(aErr.message); }
  if (!acct?.personal_space_id) return null;
  const { data: sp, error: sErr } = await supabase
    .from('spaces').select('slug').eq('id', acct.personal_space_id).maybeSingle();
  if (sErr) { console.warn('[personal] space read failed:', sErr.message); throw new Error(sErr.message); }
  return sp?.slug ?? null;
}

// ─── C10 People & Roles (admin-only) ──────────────────────────────────────────────────────────────
// All of these wrap admin/owner-gated SECURITY DEFINER RPCs (migration 20260625160000_members_roles).
// The server enforces the §6.1 removal matrix + last-owner/last-admin guards and returns a STATUS
// STRING (never raises) so the panel can toast. A non-admin caller gets an empty roster / 'forbidden'.

/**
 * The space's rich admin roster (one row per account): { account_id, display_name, avatar_url, email,
 * role (owner/admin/member), status, joined_at, updated_at }. Email is shown HERE only (not in the
 * privacy-picker pool). Admin/owner-gated server-side — a non-admin gets [].
 * @returns {Promise<Array>}
 */
export async function fetchSpaceMembersAdmin(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('space_members_admin', { p_space_slug: spaceSlug });
  if (error) { console.warn('[people] space_members_admin failed:', error.message); return []; }
  return data || [];
}

/**
 * Guests — non-member accounts holding grants on this space's live items (the Data tab "Guests"
 * table; "two-worlds audit" D2). Admin/owner-gated server-side: everyone else gets zero rows.
 * One row per account: label + claimed state, per-axis item counts + sample titles, newest
 * space-relevant grant_item_access intent (pending/expired/consumed) and item-share email status.
 */
export async function fetchSpaceItemGuests(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('space_item_guests', { p_space_slug: spaceSlug });
  if (error) { console.warn('[people] space_item_guests failed:', error.message); return []; }
  return data || [];
}

/**
 * Invite-response stats for a space (admin/owner-gated server-side; non-admins get zeros).
 * Denominator is the people we actually INVITED — distinct accounts with a source='invite' grant —
 * not everyone the space knows about, so a directory full of never-contacted profiles doesn't make
 * every campaign look broken. Returns { ever_invited, claimed, participating, claimed_silent,
 * no_response, bounced, stalled, no_delivery_data, ready_to_invite } or null when unavailable.
 * ready_to_invite is the step BEFORE the campaign — unclaimed items holding a valid creator_email —
 * and is computed server-side because those addresses are admin-gated cells the client never loads.
 */
export async function fetchSpaceInviteStats(spaceSlug) {
  if (!supabase || !spaceSlug) return null;
  const { data, error } = await supabase.rpc('space_invite_stats', { p_space_slug: spaceSlug });
  if (error) { console.warn('[people] space_invite_stats failed:', error.message); return null; }
  return Array.isArray(data) ? (data[0] || null) : (data || null);
}

/** Promote/demote across member↔admin↔owner (matrix-enforced). Returns the server status string. */
export async function setSpaceMemberRole(spaceSlug, accountId, role) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('set_space_member_role',
    { p_space_slug: spaceSlug, p_account: accountId, p_role: role });
  if (error) { console.warn('[people] set_space_member_role failed:', error.message); return null; }
  return data;
}

/** Soft-remove a member (status→removed, matrix-enforced). Returns the server status string. */
export async function removeSpaceMember(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('remove_space_member',
    { p_space_slug: spaceSlug, p_account: accountId });
  if (error) { console.warn('[people] remove_space_member failed:', error.message); return null; }
  return data;
}

/** Approve a knock-door applicant (applied→active). Returns the server status string. */
export async function approveSpaceApplicant(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('approve_space_applicant',
    { p_space_slug: spaceSlug, p_account: accountId });
  if (error) { console.warn('[people] approve_space_applicant failed:', error.message); return null; }
  return data;
}

/** Deny a knock-door applicant (applied→removed). Returns the server status string. */
export async function denySpaceApplicant(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('deny_space_applicant',
    { p_space_slug: spaceSlug, p_account: accountId });
  if (error) { console.warn('[people] deny_space_applicant failed:', error.message); return null; }
  return data;
}

/** Suspend a member (active→suspended). Returns the server status string. */
export async function suspendSpaceMember(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('suspend_space_member',
    { p_space_slug: spaceSlug, p_account: accountId });
  if (error) { console.warn('[people] suspend_space_member failed:', error.message); return null; }
  return data;
}

/** Reactivate a suspended member (suspended→active). Returns the server status string. */
export async function reactivateSpaceMember(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('reactivate_space_member',
    { p_space_slug: spaceSlug, p_account: accountId });
  if (error) { console.warn('[people] reactivate_space_member failed:', error.message); return null; }
  return data;
}

/**
 * Leave a space — the one membership mutation whose subject is the CALLER, so it needs no admin
 * gate (auth.uid() inside the RPC is both). Flips your own memberships in that space to
 * status='left' and also covers a pending invite/application, so it doubles as decline/withdraw.
 *
 * Exists because inviting an already-claimed email auto-adds at status='active' with no consent
 * step (api/workspace/[slug]/members/invite.js) — the exit is the mitigation for that. The server
 * keeps the §6.1 last-owner / last-admin guards, so this can't orphan a space, and refuses your
 * own Personal space.
 *
 * Returns the server status string ('left' on success). Callers should treat anything else as a
 * refusal and show `leaveSpaceMessage(status)`.
 */
export async function leaveSpace(spaceSlug) {
  if (!supabase || !spaceSlug) return null;
  const { data, error } = await supabase.rpc('leave_space', { p_space_slug: spaceSlug });
  if (error) { console.warn('[spaces] leave_space failed:', error.message); return null; }
  return data;
}

/** leave_space()'s refusal codes → a sentence to show the person who clicked Leave. */
export function leaveSpaceMessage(status) {
  switch (status) {
    case 'last-owner':         return 'You’re the only owner — make someone else an owner first, so the space isn’t left without one.';
    case 'last-admin':         return 'You’re the only admin — make someone else an admin first, so the space isn’t left unmanaged.';
    case 'own-personal-space': return 'This is your own personal space, so there’s nothing to leave.';
    case 'not-a-member':       return 'You’re not a member of this space.';
    case 'no-space':           return 'That space no longer exists.';
    case 'not-authenticated':  return 'Sign in to leave a space.';
    default:                   return 'Could not leave the space. Try again.';
  }
}

/**
 * Star/unstar a space for the current account. Self-only — favorites live in
 * `account_preferences.favorite_space_ids` (one row per account), and the RPC does the array
 * mutation in a single atomic statement so two tabs toggling different spaces can't clobber each
 * other. Requires a live membership in the space. Returns 'favorited' | 'unfavorited' | a refusal
 * code.
 */
export async function setSpaceFavorite(spaceSlug, favorite) {
  if (!supabase || !spaceSlug) return null;
  const { data, error } = await supabase.rpc('set_space_favorite',
    { p_space_slug: spaceSlug, p_favorite: !!favorite });
  if (error) { console.warn('[spaces] set_space_favorite failed:', error.message); return null; }
  return data;
}

/**
 * The signed-in account's spaces — one row per space as { slug, name, status, favorite }
 * where status is 'active' | 'applied' | 'invited'. Powers the space switcher's
 * Favorites/My/Applied/Invited filters. Applied/invited spaces aren't readable via
 * spaces_read RLS, hence the SECURITY DEFINER my_spaces() RPC. `favorite` is computed
 * server-side (favorites are keyed by space id, the switcher works in slugs) so the
 * switcher needs one call, not a join. Anonymous sessions get [] (auth.uid() is null
 * inside the RPC).
 */
export async function fetchMySpaces() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('my_spaces');
  if (error) { console.warn('[switcher] my_spaces failed:', error.message); return []; }
  return data || [];
}

// ─── People & Groups admin (custom groups + admin profile-linking) ──────────────────────────────────
// Admin/owner-gated SECURITY DEFINER RPCs (migration 20260702100000_people_groups_admin). Custom-group
// membership carries no governance meaning — ROLE changes still flow through setSpaceMemberRole and its
// §6.1 matrix; these never touch the owners/admins/members slots.

/** Per-account custom-group slugs/labels + linked profile for the roster grid. Admin-gated → [] otherwise. */
export async function fetchSpaceMemberGroups(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('space_member_group_map', { p_space_slug: spaceSlug });
  if (error) { console.warn('[people] space_member_group_map failed:', error.message); return []; }
  return data || [];
}

/** The space's custom (non-role-slot) groups + active member counts. Admin-gated → [] otherwise. */
export async function fetchSpaceAssignableGroups(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('space_assignable_groups', { p_space_slug: spaceSlug });
  if (error) { console.warn('[people] space_assignable_groups failed:', error.message); return []; }
  return data || [];
}

// Active-member count per group (role slots + custom) for the privacy picker. Member-gated;
// returns a { slug: count } map. Empty for non-members / on error.
export async function fetchSpaceGroupCounts(spaceSlug) {
  if (!supabase || !spaceSlug) return {};
  const { data, error } = await supabase.rpc('space_group_counts', { p_space_slug: spaceSlug });
  if (error) { console.warn('[picker] space_group_counts failed:', error.message); return {}; }
  const map = {};
  for (const r of data || []) map[r.slug] = r.member_count;
  return map;
}

/** Mint a custom group. Returns { status, slug?, display_name? } — status 'created' on success. */
export async function createSpaceGroup(spaceSlug, displayName) {
  if (!supabase) return { status: null };
  const { data, error } = await supabase.rpc('create_space_group',
    { p_space_slug: spaceSlug, p_display_name: displayName });
  if (error) { console.warn('[people] create_space_group failed:', error.message); return { status: null }; }
  return data || { status: null };
}

/**
 * Every group of a space as the People & groups admin table draws it, with who is in each.
 *
 * Plain table reads: `groups_read` and `gm_space_read` already let an active member of the space
 * read both, and the admin gate lives on every write. Link groups (a note's shared link) are left
 * out, as everywhere else that lists a space's groups.
 *
 * `rows` carries invited and requested people as well as active ones, because the group's roster
 * shows all three — a pending invite is the only trace that someone is on their way in.
 *
 * @returns {Promise<{ groups: object[], rows: Array<{group_id, account_id, status, profile_item_id}> }>}
 */
export async function fetchSpaceGroupsAdmin(spaceSlug) {
  const spaceId = await spaceIdForSlug(spaceSlug);
  if (!supabase || !spaceId) return { groups: [], rows: [] };
  const [g, m] = await Promise.all([
    supabase.from('groups')
      .select('id, slug, display_name, description, color, icon_url, is_system, lifecycle, join_policy, roster_visibility, item_id, link_item_id, created_at')
      .eq('primary_space_id', spaceId),
    supabase.from('group_members')
      .select('group_id, account_id, status, profile_item_id')
      .eq('primary_space_id', spaceId)
      .in('status', ['active', 'invited', 'applied']),
  ]);
  if (g.error) console.warn('[people] groups read failed:', g.error.message);
  if (m.error) console.warn('[people] group_members read failed:', m.error.message);
  const groups = (g.data || []).filter((row) => !row.link_item_id && !isLinkGroupSlug(row.slug));
  return { groups, rows: m.data || [] };
}

/**
 * Save a group's own settings. `groups_admin_update` (migration 20260723120000) lets an admin or
 * owner of the space write the row; only the keys below are ever sent, so a slug or the system flag
 * cannot change from here.
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
const GROUP_SETTING_KEYS = ['display_name', 'description', 'color', 'icon_url', 'join_policy', 'roster_visibility', 'lifecycle'];
export async function updateSpaceGroup(groupId, patch) {
  if (!supabase || !groupId) return { ok: false, error: 'not-configured' };
  const clean = Object.fromEntries(Object.entries(patch || {}).filter(([k]) => GROUP_SETTING_KEYS.includes(k)));
  if (!Object.keys(clean).length) return { ok: true };
  const { data, error } = await supabase.from('groups')
    .update({ ...clean, updated_at: new Date().toISOString() })
    .eq('id', groupId).select('id');
  if (error) return { ok: false, error: error.message };
  // RLS turns a refused update into zero rows rather than an error.
  if (!data?.length) return { ok: false, error: 'not-saved' };
  return { ok: true };
}

/**
 * The people waiting on a decision to join one of the space's custom groups, with their email
 * (migration 20261004120000). Admin only; anybody else gets [].
 */
export async function fetchGroupApplications(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('group_applications', { p_space_slug: spaceSlug });
  if (error) { console.warn('[people] group_applications failed:', error.message); return []; }
  return data || [];
}

/** Approve or decline a request to join a group. Returns 'active' | 'removed' | a refusal. */
export async function decideGroupApplication(spaceSlug, groupSlug, accountId, approve) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('decide_group_application', {
    p_space_slug: spaceSlug, p_group_slug: groupSlug, p_account: accountId, p_approve: approve,
  });
  if (error) { console.warn('[people] decide_group_application failed:', error.message); return null; }
  return data;
}

/**
 * Add/remove a person in a CUSTOM group. Returns the server status string.
 *
 * 'added' | 'removed' | 'noop' on success. The two refusals worth handling by name:
 *   'role-slot-group' — owners/admins/members are set with the role RPCs, not as groups.
 *   'system-group'    — the group is a PLATFORM group (curators, arcos-alpha, …). Its
 *                       membership is granted on Data → Groups → Platform behind
 *                       is_superadmin(), never from one space's admin screen
 *                       (migration 20260812000917). Report it; don't fold it into a
 *                       generic skipped count — it means "wrong surface", not "no change".
 */
export async function setGroupMembership(spaceSlug, accountId, groupSlug, active) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('set_group_membership',
    { p_space_slug: spaceSlug, p_account: accountId, p_group_slug: groupSlug, p_active: active });
  if (error) { console.warn('[people] set_group_membership failed:', error.message); return null; }
  return data;
}

/** Link/unlink another member's profile item (itemId null unlinks). Returns 'ok'|'unlinked'|error. */
export async function adminSetMemberProfileItem(spaceSlug, accountId, itemId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('admin_set_profile_item',
    { p_space_slug: spaceSlug, p_account: accountId, p_item_id: itemId });
  if (error) { console.warn('[prof] admin_set_profile_item failed:', error.message); return null; }
  return data;
}

/**
 * The per-user answers on a profile that were written by someone who is NOT its current holder —
 * grouped by author, with what would happen to each if they moved.
 *
 * Repointing a profile cannot carry these by itself: a `one_per_user` answer is editable by its
 * AUTHOR alone (`can_edit_cell_row` takes the "own your answer" branch and never consults the
 * field's `@parent-editors`), and these fields are usually admin-excluded too, so the new holder
 * can read the answers and edit none of them. The roster asks whether to move them rather than
 * moving them on link — see migration 20261006120000 for why that is not automatic.
 *
 * Canonical fields are excluded by the function, so a third party's Viewed / Favorited / Progress /
 * My Status cell on someone's profile is never counted and never offered.
 *
 * @returns {Promise<Array<{author_id, author_name, author_email, movable, duplicates, conflicts, field_names}>>}
 */
export async function fetchProfileStrandedAnswers(spaceSlug, itemId) {
  if (!supabase || !itemId) return [];
  const { data, error } = await supabase.rpc('profile_stranded_answers',
    { p_space_slug: spaceSlug, p_item_id: itemId });
  if (error) { console.warn('[prof] profile_stranded_answers failed:', error.message); return []; }
  return data || [];
}

/**
 * Re-point those answers onto the profile's CURRENT holder. The destination is deliberately not a
 * parameter — the server reads it from the link — so this can only ever gather a profile's answers
 * onto the account the profile names.
 *
 * Three refusals worth handling by name, all returned rather than thrown:
 *   'conflict'   — the holder already answered one of those fields DIFFERENTLY. Nothing is written;
 *                  which answer survives is a person's decision.
 *   'duplicates' — the holder re-answered a question they could not edit, with an identical value.
 *                  Re-call with dropIdentical to delete the stale copy.
 *   'not-linked' — the item is nobody's profile any more.
 *
 * @returns {Promise<{status: string, moved?: number, dropped?: number, conflicts?: number} | null>}
 */
export async function moveProfileAnswers(spaceSlug, itemId, fromAccountId, dropIdentical = false) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('move_profile_answers',
    { p_space_slug: spaceSlug, p_item_id: itemId, p_from: fromAccountId, p_drop_identical: dropIdentical });
  if (error) { console.warn('[prof] move_profile_answers failed:', error.message); return null; }
  return data;
}

// ─── Platform groups (superadmin-gated) ───────────────────────────────────────────────────────────
// The groups that carry power across the whole platform, not inside one space: the space-less system
// groups (arcos-alpha, arcos-admins) plus system groups hosted in a space (curators, which lives in
// commons-curators). Migrations 20260812001042 (is_superadmin) + 20260812001158 (the RPCs).
//
// Every call here is a SECURITY DEFINER RPC because the underlying rows are unreadable by design:
// group_members' only SELECT policies are gm_self_read and gm_space_read(primary_space_id), and a
// space-less membership satisfies neither — so a platform roster has never been client-readable.
// Non-superadmins get empty results rather than errors, so a stray call is quiet, not noisy.

/** True if the signed-in account holds the platform_admin capability. Gates the Platform scope chip. */
export async function isSuperadmin() {
  if (!supabase) return false;
  const { data, error } = await supabase.rpc('is_superadmin');
  if (error) { console.warn('[platform] is_superadmin failed:', error.message); return false; }
  return data === true;
}

/**
 * The signed-in person's own capability tags (create_space, platform_admin, builder, …), for the
 * switcher's Build row (src/features/build/buildTools.js). `group_capabilities` has no read policy, so
 * this goes through the my_capabilities() definer function (migration 20261118181207). A failure reads
 * as no tags, which hides the row: every tool behind it keeps its own server check regardless.
 * @returns {Promise<string[]>}
 */
export async function fetchMyCapabilities() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('my_capabilities');
  if (error) { console.warn('[platform] my_capabilities failed:', error.message); return []; }
  return Array.isArray(data) ? data : [];
}

/**
 * The platform groups + capability tags + active member counts. Excludes the 13 computed audience
 * tokens (@everyone, @parent-viewers, …): they are evaluated at read time and always have 0 members,
 * so listing them in a membership UI would invite an Add-member click that can never mean anything.
 * @returns {Promise<Array<{id,slug,display_name,description,scope,space_slug,capabilities,member_count}>>}
 */
export async function fetchPlatformGroups() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('platform_groups');
  if (error) { console.warn('[platform] platform_groups failed:', error.message); return []; }
  return data || [];
}

/**
 * One platform group's roster: who is in it, their status, when they joined, and who granted it.
 * @returns {Promise<Array<{account_id,display_name,email,account_type,status,joined_at,granted_by,granted_by_name}>>}
 */
export async function fetchPlatformGroupMembers(groupSlug) {
  if (!supabase || !groupSlug) return [];
  const { data, error } = await supabase.rpc('platform_group_members', { p_group_slug: groupSlug });
  if (error) { console.warn('[platform] platform_group_members failed:', error.message); return []; }
  return data || [];
}

/**
 * Search ALL accounts by name or email (2+ chars, max 20) for the Add-member picker.
 *
 * This inverts the direction the Members tab works in. A space group is filled by ticking people who
 * are already in the space; a platform group has no space roster to tick, so the picker searches the
 * whole account table — which needs a definer RPC, since `accounts` is self-read-only under RLS.
 */
export async function searchPlatformAccounts(query) {
  if (!supabase || !query || query.trim().length < 2) return [];
  const { data, error } = await supabase.rpc('platform_account_search', { p_query: query.trim() });
  if (error) { console.warn('[platform] platform_account_search failed:', error.message); return []; }
  return data || [];
}

/**
 * Add an account to a platform group.
 * @returns {Promise<string|null>} 'added' | 'noop' | 'self-grant' | 'no-group' | 'no-account' |
 *   'forbidden' | 'not-authenticated'. 'self-grant' is the deliberate refusal to make YOURSELF a
 *   platform admin — every platform admin is made one by somebody else.
 */
export async function grantPlatformGroup(groupSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('grant_platform_group',
    { p_group_slug: groupSlug, p_account_id: accountId });
  if (error) { console.warn('[platform] grant_platform_group failed:', error.message); return null; }
  return data;
}

/**
 * Remove an account from a platform group.
 * @returns {Promise<string|null>} 'removed' | 'noop' | 'last-admin' | 'no-group' | 'forbidden' |
 *   'not-authenticated'. 'last-admin' guards the platform against having no admin left at all.
 */
export async function revokePlatformGroup(groupSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('revoke_platform_group',
    { p_group_slug: groupSlug, p_account_id: accountId });
  if (error) { console.warn('[platform] revoke_platform_group failed:', error.message); return null; }
  return data;
}

// ─── Create Arcos Space (system-group gated) ──────────────────────────────────────────────────────
// A NULL-space system group ("Arcos Alpha") carries the 'create_space' capability; members may mint
// new Supabase-native spaces. Both are SECURITY DEFINER RPCs (migration 20260714162122). Distinct from
// is_superadmin() — this is a narrow, grantable "space creator" power, not the platform god-tier.

/** True if the signed-in account may create Supabase-native spaces (active member of a create_space group). */
export async function canCreateSpace() {
  if (!supabase) return false;
  const { data, error } = await supabase.rpc('can_create_space');
  if (error) { console.warn('[space] can_create_space failed:', error.message); return false; }
  return data === true;
}

/**
 * Where the caller stands on creating spaces (migration 20261029090000): 'granted' · 'applied' (asked,
 * waiting for a platform admin) · 'removed' (declined or removed; asking again does not overrule it) ·
 * 'requestable' · 'closed' (no group accepts requests). Until that migration is deployed the RPC does
 * not exist, so this answers from can_create_space() alone rather than greying the row for everyone.
 */
export async function fetchCreateSpaceAccess() {
  if (!supabase) return 'closed';
  const { data, error } = await supabase.rpc('create_space_access');
  if (!error && typeof data === 'string') return data;
  return (await canCreateSpace()) ? 'granted' : 'closed';
}

/** Ask to join the group that may create spaces. Returns the standing afterwards, or 'error'. */
export async function requestCreateSpaceAccess() {
  if (!supabase) return 'error';
  const { data, error } = await supabase.rpc('request_create_space_access');
  if (error) { console.warn('[space] request_create_space_access failed:', error.message); return 'error'; }
  return data;
}

/** Superadmin: approve or decline a request to create spaces. Returns 'active' · 'removed' · a refusal. */
export async function decideCreateSpaceRequest(accountId, approve) {
  if (!supabase) return 'error';
  const { data, error } = await supabase.rpc('decide_create_space_request', { p_account: accountId, p_approve: !!approve });
  if (error) { console.warn('[space] decide_create_space_request failed:', error.message); return 'error'; }
  return data;
}

/**
 * Provision a new Supabase-backed space (server enforces can_create_space + slug rules). On success the
 * caller is its admin, so they can navigate straight in. Returns the new space row or a friendly error.
 * @returns {Promise<{ ok: boolean, space?: object, error?: string }>}
 */
export async function createArcosSpace(name, slug) {
  if (!supabase) return { ok: false, error: 'not-configured' };
  const { data, error } = await supabase.rpc('create_space', { p_name: name, p_slug: slug });
  if (error) {
    // Postgres RAISE messages surface in error.message (e.g. 'slug "x" is already taken').
    console.warn('[space] create_space failed:', error.message);
    return { ok: false, error: error.message || 'Could not create space' };
  }
  // returns setof spaces → single-row array (or the row object depending on PostgREST shape).
  const space = Array.isArray(data) ? data[0] : data;
  return { ok: true, space };
}

// ─── C10b Email invites (admin-only) ──────────────────────────────────────────────────────────────
// Issuing an invite needs the SERVICE ROLE (mint the unclaimed account + generate the magic-link), so
// it goes through the SERVER endpoint api/workspace/[slug]/members/invite — NOT a client RPC. The
// client forwards its session bearer so the endpoint can gate the caller as admin/owner under RLS.
// Redeem + revoke are SECURITY DEFINER RPCs (migration 20260625180000_invite_engine).

/**
 * Delete a workspace (Supabase-native) field — admin/owner-gated server-side.
 * @param {string} spaceSlug
 * @param {{ key: string, storage?: 'cell'|'jsonb', withValues: boolean }} opts
 *   - key        = the field key/name (the sidebar's fieldId; the exact custom_fields jsonb key).
 *   - storage    = 'cell' for cell-backed fields, else jsonb/column. Hints the value-wipe path.
 *   - withValues = true → also erase the data (jsonb key strip, or cell cascade). false → registry only.
 * @returns {Promise<{ ok: boolean, deletedValues?: number, error?: string }>}
 */
export async function deleteWorkspaceField(spaceSlug, { key, storage, withValues } = {}) {
  if (!supabase || !spaceSlug || !key) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/delete-field`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ key, storage, withValues: !!withValues }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * Issue an email invite to a space (admin/owner-gated server-side). If a claimed account already
 * exists for the email it's added directly; otherwise an unclaimed account + invited membership +
 * claim_intent are created and a magic-link (carrying ?ct=<token>) is emailed (dev→Mailpit).
 * @returns {Promise<{ ok: boolean, status?: string, role?: string, emailed?: boolean, error?: string }>}
 */
export async function inviteSpaceMember(spaceSlug, email, role, opts = {}) {
  if (!supabase || !spaceSlug) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/members/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        // A connection is invited by account: the server looks its address up, because the admin
        // was never shown it (api/workspace/[slug]/members/invite.js).
        ...(opts.accountId ? { account_id: opts.accountId } : { email }),
        role,
        // invite-to-claim extensions (optional; see api/workspace/[slug]/members/invite.js)
        ...(opts.profileItemId ? { profile_item_id: opts.profileItemId } : {}),
        // 'profile' (you ARE the item) vs 'steward' (you manage it — projects/needs/offers)
        ...(opts.claimKind ? { claim_kind: opts.claimKind } : {}),
        ...(Array.isArray(opts.groupSlugs) && opts.groupSlugs.length ? { group_slugs: opts.groupSlugs } : {}),
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * Re-send a pending invite. There is no separate endpoint: the invite endpoint is already idempotent
 * (it reuses the existing unclaimed account, upserts the membership so invited_at is preserved,
 * retires the superseded claim_intent, mints a fresh magic-link and re-sends). Preserve the invite's
 * shape when resending — `profileItemId` keeps it a "claim your profile" email rather than silently
 * demoting it to a plain space invite, and `groupSlugs` keeps any cohort memberships attached.
 *
 * Each resend writes a new source='invite' email_messages row, so last_invite_sent_at on the roster
 * moves and the new send is tracked independently of the original.
 *
 * @returns {Promise<{ ok: boolean, status?: string, emailed?: boolean, error?: string }>}
 */
export async function resendSpaceInvite(spaceSlug, { email, role, profileItemId, groupSlugs } = {}) {
  if (!email) return { ok: false, error: 'no-email' };
  return inviteSpaceMember(spaceSlug, email, role || 'member', { profileItemId, groupSlugs });
}

/**
 * Bulk invite: invite many emails to a space with one role. Loops `inviteSpaceMember` SEQUENTIALLY —
 * the per-email endpoint already match-or-mints idempotently, and sequential avoids hammering the
 * auth admin API / mailer + sidesteps any mint races. Dedupes by normalized email. `onProgress({done,
 * total})` fires after each. Returns a per-email result list `[{ email, ok, status, emailed, error }]`
 * the caller renders as a report. v1 = a handful at a time; a server batch endpoint can replace this
 * loop later without changing the call site.
 */
export async function inviteSpaceMembers(spaceSlug, emails, role, onProgress) {
  const unique = [...new Set((emails || []).map((e) => String(e).trim().toLowerCase()).filter(Boolean))];
  const results = [];
  for (let i = 0; i < unique.length; i++) {
    const email = unique[i];
    // sequential on purpose (see docstring) — avoids hammering the admin API / mailer + mint races
    const r = await inviteSpaceMember(spaceSlug, email, role);
    results.push({ email, ...r });
    onProgress?.({ done: i + 1, total: unique.length });
  }
  return results;
}

/**
 * Redeem a pending invite for the signed-in caller by token (the ?ct=<token> on the landing URL).
 * Flips the invited membership → active + stamps claimed_at server-side. Returns the status string
 * ('redeemed' | 'already-redeemed' | 'no-pending-invite' | 'invite-revoked' | ...). Best-effort.
 */
export async function redeemInvite(token) {
  if (!supabase || !token) return null;
  const { data, error } = await supabase.rpc('redeem_invite', { p_token: token });
  if (error) { console.warn('[invite] redeem_invite failed:', error.message); return null; }
  return data;
}

/**
 * Invite-to-claim: which profile items are claimed or have a pending invite, by whom. Admin-gated →
 * {} otherwise. Returns { [profileItemId]: { accountId, memberStatus: 'invited'|'applied'|'active', email, displayName } }.
 */
export async function fetchSpaceProfileClaims(spaceSlug) {
  if (!supabase || !spaceSlug) return {};
  const { data, error } = await supabase.rpc('space_profile_claims', { p_space_slug: spaceSlug });
  if (error) { console.warn('[invite] space_profile_claims failed:', error.message); return {}; }
  const map = {};
  for (const r of data || []) {
    map[r.profile_item_id] = { accountId: r.account_id, memberStatus: r.member_status, email: r.email, displayName: r.display_name };
  }
  return map;
}

/**
 * Invite-to-claim: read the canonical `creator_email` CELL values for a set of items. The field is
 * a system row (primary_space_id NULL, value_storage='cell') whose cells are RLS-gated to
 * admins + @parent-editors (migration 20260723120000) — a plain viewer gets zero rows here, which
 * is the whole point of cell storage over the old custom_fields jsonb key.
 *
 * The key lowercased in 20260804212147 (canonical keys are snake_case: title/description/email/…).
 * Both spellings are accepted so a deploy that lands before or after that migration still resolves
 * the field — the rename is an UPDATE, so only ever one row matches. Drop the old spelling once
 * prod is migrated.
 * Returns { [itemId]: email }; {} when the field isn't provisioned yet (pre-migration fallback:
 * callers also read the legacy jsonb key).
 */
export async function fetchCreatorEmailCells(itemIds) {
  if (!supabase || !Array.isArray(itemIds) || !itemIds.length) return {};
  // The canonical registry key was snake_cased to 'creator_email' after the original
  // 'Creator Email' migration — match both so the cell read survives either state.
  const { data: fieldRows, error: fErr } = await supabase
    .from('fields').select('id')
    .is('primary_space_id', null).eq('shown_on', 'item').is('shown_on_id', null)
    .in('key', ['creator_email', 'Creator Email']);
  if (fErr || !fieldRows?.length) return {};
  const { data, error } = await supabase
    .from('cells').select('subject_item_id, value_text')
    .in('field_id', fieldRows.map((f) => f.id)).in('subject_item_id', itemIds);
  if (error) { console.warn('[invite] creator-email cells read failed:', error.message); return {}; }
  const map = {};
  for (const r of data || []) if (r.value_text) map[r.subject_item_id] = r.value_text;
  return map;
}

/** Revoke a still-pending invite (admin/owner-gated; hard-removes membership + intent + unclaimed account). */
export async function revokeSpaceInvite(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('revoke_invite',
    { p_space_slug: spaceSlug, p_account: accountId });
  if (error) { console.warn('[invite] revoke_invite failed:', error.message); return null; }
  return data;
}

// ─── Item-access-by-email (verb 3) ──────────────────────────────────────────────────────────────
/**
 * Share item(s) with someone by email — grant view/edit. A non-member is matched-or-minted and
 * emailed a magic-link that deep-links to the item (grant written at issue); an existing claimed
 * account is granted directly + emailed a heads-up. Admin-only `alsoAddAsMember` additionally makes
 * them an active space member. Gated server-side on the caller being able to EDIT each item.
 * @returns {Promise<{ ok:boolean, status?:string, accountId?:string, granted?:string[], addedMember?:boolean, emailed?:boolean, error?:string }>}
 */
export async function shareItemByEmail(spaceSlug, { itemIds, email, axis = 'viewer', alsoAddAsMember = false }) {
  if (!supabase || !spaceSlug) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/items/share-by-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ itemIds, email, axis, alsoAddAsMember }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * Item Share Modal — the batch Send pipeline (PR-3): grant the whole chip set (existing accounts +
 * space groups + free-text emails) in ONE POST, with the Notify toggle + optional message. Server
 * writes every grant (edit-gated as caller) and sends the §3-matrix emails — claimed recipients get
 * the heads-up only if notify; unclaimed always get the claim email (it carries the access).
 * @returns {Promise<{ ok:boolean, results?:Array, error?:string }>}
 */
export async function shareItemsBatch(spaceSlug, { itemIds, accountIds = [], groups = [], emails = [], axis = 'viewer', notify = true, message } = {}) {
  if (!supabase || !spaceSlug) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/items/share-by-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ itemIds, accountIds, groups, emails, axis, notify, message }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}`, results: body.results };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * Related-tab "why related" reasons — one short AI line per candidate (seed + candidate IDs from the
 * RLS-scoped related_items RPC). Display sugar; returns `{}` on any failure so the tab still renders.
 * @returns {Promise<Record<string,string>>} map of itemId → reason
 */
/**
 * @returns {Promise<{ reasons: Record<string,string>, error?: string }>} `error` is only
 * present when the AI call itself failed (vs. succeeding with no groundable reasons) — see
 * RelatedTab.jsx, which uses its presence to distinguish "genuinely nothing to say" from
 * "the AI call broke" so it can surface the latter instead of failing silently.
 */
/**
 * The people who share my answer to a private field (GET /api/workspace/[slug]/field-mates).
 * Cached per slug and field for a minute: the dashboard re-resolves its sources whenever the graph
 * changes, and group membership does not move that fast. Returns { status, group?, mateIds? } or
 * { error }.
 */
const _fieldMatesCache = new Map();
export async function fetchFieldMates(spaceSlug, fieldKey) {
  if (!supabase || !spaceSlug || !fieldKey) return { error: 'missing' };
  const key = `${spaceSlug}:${fieldKey}`;
  const hit = _fieldMatesCache.get(key);
  if (hit && Date.now() - hit.at < 60_000) return hit.promise;
  const promise = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { status: 'no-profile' };
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/field-mates?field=${encodeURIComponent(fieldKey)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { _fieldMatesCache.delete(key); return { error: body.error || `HTTP ${res.status}` }; }
    return body;
  })().catch((err) => { _fieldMatesCache.delete(key); return { error: err.message || 'Request failed' }; });
  _fieldMatesCache.set(key, { at: Date.now(), promise });
  return promise;
}

export async function explainRelated(spaceSlug, { seedId, candidateIds, seedFacet, candidateFacet } = {}) {
  if (!supabase || !spaceSlug || !seedId || !candidateIds?.length) return { reasons: {} };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { reasons: {} };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/items/related-why`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // The facets the RANKING used, so the endpoint can brief from the same text it compared
      // rather than from the whole item — see related-why.js.
      body: JSON.stringify({ seedId, candidateIds, seedFacet, candidateFacet }),
    });
    const body = await res.json().catch(() => ({}));
    // A refused visitor limit still carries whatever reasons the server had cached.
    return { reasons: body.reasons || {}, error: rateLimitMessage(res.status, body) || body.error };
  } catch (err) {
    return { reasons: {}, error: err.message || 'Request failed' };
  }
}

/**
 * Search All — free-text semantic search across the space. Server embeds the query and calls the
 * RLS-scoped search RPC `asCaller`, so results are only items you can view. Returns ranked IDs +
 * distance (the view resolves them against loaded nodes, like the Related tab). [] on any failure.
 * Pass scope:'all' to search every space the caller can see (one global kNN, exact ranking —
 * can_view still gates per row); the slug then only anchors the route.
 * Pass typeIds (item type uuids) to restrict to those types — the filter applies BEFORE the
 * server's top-k, so it returns the best k items OF THAT TYPE, not a subset of the blended k.
 * @returns {Promise<Array<{ itemId: string, distance: number }>>}
 */
/**
 * Tell a space's admins that something failed. Best-effort by design: this is already the unhappy
 * path, and a failure to report a failure must not become a second error in front of the person.
 */
export async function reportSpaceError(spaceSlug, { feature, detail, note } = {}) {
  if (!supabase || !spaceSlug || !feature) return false;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return false;
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/report-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ feature, detail, ...(note ? { note } : {}) }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function searchSpace(spaceSlug, { query, k = 24, scope = 'space', typeIds } = {}) {
  if (!supabase || !spaceSlug || !query?.trim()) return { items: [], error: null };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { items: [], error: 'Sign in to search this space.' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ query, k, scope, ...(typeIds?.length ? { typeIds } : {}) }),
    });
    const body = await res.json().catch(() => ({}));
    // The endpoint answers 200 with `{ items: [], error }` when the search itself failed — a bad
    // embeddings key, a rate limit, a model outage. Returning only `items` threw that away, and the
    // caller rendered the empty array as "No matches", so a broken search and a genuinely empty one
    // were indistinguishable. Real example: a 401 from OpenAI read as "no matches for climate" in a
    // space with 1,306 climate events.
    // `limited` is the visitor limit's own sentence, kept apart so the view can show it as written
    // instead of dressing it up as a broken search.
    const limited = rateLimitMessage(res.status, body);
    return { items: body.items || [], error: limited || body.error || (res.ok ? null : `Search failed (${res.status})`), limited };
  } catch (err) {
    return { items: [], error: err?.message || 'Search is unavailable right now.' };
  }
}

/**
 * Search All — the AI summary card + per-result reasons. `itemIds` must be in the same ranked order
 * the view renders, so the summary's [n] citations map to the nth card. Display sugar; returns an
 * empty answer + reasons on any failure so the cards still render.
 * @returns {Promise<{ answer: string, reasons: Record<string,string>, error?: string, limited?: string }>}
 * `error` is only present when the AI call itself failed (vs. succeeding with a genuinely empty
 * answer) — see SearchAllCanvas.jsx, which uses its presence to surface the failure instead of
 * staying silent. When the visitor limit refused the call, `limited` carries the sentence to show.
 */
export async function explainSearch(spaceSlug, { query, itemIds, typeFilterName } = {}) {
  const empty = { answer: '', reasons: {} };
  if (!supabase || !spaceSlug || !query?.trim() || !itemIds?.length) return empty;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return empty;
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/search-answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ query, itemIds, ...(typeFilterName ? { typeFilterName } : {}) }),
    });
    const body = await res.json().catch(() => ({}));
    const limited = rateLimitMessage(res.status, body);
    return { answer: body.answer || '', reasons: body.reasons || {}, error: limited || body.error, limited };
  } catch (err) {
    return { ...empty, error: err.message || 'Request failed' };
  }
}

/**
 * P4a — request access to an item you can't fully see/edit. Files an item_access_requests row
 * (pending) + routes a delivery to the space admins. Idempotent per (item, you, capability).
 * @returns {Promise<{ ok:boolean, requestId?:string, status?:string, error?:string }>}
 */
export async function requestItemAccess(spaceSlug, { itemId, capability = 'edit', note } = {}) {
  if (!supabase || !spaceSlug) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/items/request-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ itemId, capability, note }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * P4a — an approver (item editor / space admin) decides a pending request. On approve the grant is
 * written onto the item; either way the requester gets an outcome notification.
 * @returns {Promise<{ ok:boolean, status?:string, error?:string }>}
 */
/**
 * Accept or decline an item shared into a space whose sharer could not add to it (Phase 4 of the
 * cross-space sharing plan). Only the space's admins decide; declining keeps it out even if it is
 * shared again, until an admin accepts it.
 * @param {'accept'|'reject'} decision
 * @returns {Promise<{ ok: boolean, state?: string, error?: string }>}
 */
/**
 * The Data view's Review list for a space: shares waiting for an admin, shares accepted in the last 30
 * days (`state: 'accepted'`, migration 20261109162347), and items the space removed.
 * Admins only; anyone else gets `{ ok: false }`.
 * @returns {Promise<{ ok: boolean, rows?: Array, error?: string }>}
 */
export async function fetchSpaceReview(spaceId) {
  if (!supabase || !spaceId) return { ok: false, error: 'not-configured' };
  const { data, error } = await supabase.rpc('space_inclusion_review', { p_space: spaceId });
  if (error) return { ok: false, error: error.message };
  return {
    ok: true,
    rows: (data || []).map((r) => ({
      itemId: r.r_item_id, state: r.r_state, title: r.r_title, itemTypeId: r.r_item_type_id, canOpen: !!r.r_can_open,
      ownerSpaceName: r.r_owner_space_name || null,
      sharedBy: r.r_shared_by || null, sharedByName: r.r_shared_by_name || null, sharedAt: r.r_shared_at || null,
      decidedBy: r.r_decided_by || null, decidedByName: r.r_decided_by_name || null, decidedAt: r.r_decided_at || null,
      stillShared: !!r.r_still_shared,
      sharedWith: r.r_shared_with || [], isPublic: !!r.r_is_public,
    })),
  };
}

/** Forget a removal the owner has since withdrawn, so a future share arrives fresh. Admins only. */
export async function forgetSpaceInclusion(spaceId, itemId) {
  if (!supabase || !spaceId || !itemId) return { ok: false, error: 'not-configured' };
  const { error } = await supabase.rpc('space_inclusion_forget', { p_space: spaceId, p_item: itemId });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function decideSpaceInclusion(spaceId, itemId, decision) {
  if (!supabase || !spaceId || !itemId) return { ok: false, error: 'not-configured' };
  const { data, error } = await supabase.rpc('space_inclusion_decide', { p_space: spaceId, p_item: itemId, p_decision: decision });
  if (error) return { ok: false, error: error.message };
  return { ok: true, state: data };
}

/**
 * Approval (Add System Phase 7, migration 20261109090000). A space admin approves or rejects an item
 * that waits; the note goes to its creator. Only these two calls change the approval columns.
 */
export async function approveItem(itemId, note = null) {
  if (!supabase || !itemId) return { ok: false, error: 'not-configured' };
  const { data, error } = await supabase.rpc('approve_item', { p_item: itemId, p_note: note || null });
  if (error) return { ok: false, error: error.message };
  return { ok: true, status: data };
}

export async function rejectItem(itemId, note = null) {
  if (!supabase || !itemId) return { ok: false, error: 'not-configured' };
  const { data, error } = await supabase.rpc('reject_item', { p_item: itemId, p_note: note || null });
  if (error) return { ok: false, error: error.message };
  return { ok: true, status: data };
}

/**
 * The new items held in a space: waiting, then not approved. Row security returns all of them to an
 * admin and only their own to anyone else, so the same read serves the Review tab and a creator.
 * Creator names come from `actor_labels`, the resolver the item load uses.
 */
export async function fetchHeldItems(spaceId) {
  if (!supabase || !spaceId) return { ok: false, error: 'not-configured' };
  const { data, error } = await supabase.from('items')
    .select('id, title, item_type_id, created_by, created_at, approval_status, approved_by, approved_at, rejection_note')
    .eq('primary_space_id', spaceId).eq('is_deleted', false)
    .in('approval_status', ['pending', 'rejected'])
    .order('created_at', { ascending: false }).limit(500);
  if (error) return { ok: false, error: error.message };
  const ids = [...new Set((data || []).flatMap((r) => [r.created_by, r.approved_by]).filter(Boolean))];
  const names = new Map();
  if (ids.length) {
    const { data: labels } = await supabase.rpc('actor_labels', { p_ids: ids });
    for (const l of labels || []) names.set(l.id, l.display_name || null);
  }
  return {
    ok: true,
    rows: (data || []).map((r) => ({
      itemId: r.id, title: r.title, itemTypeId: r.item_type_id, status: r.approval_status,
      createdBy: r.created_by, createdByName: names.get(r.created_by) || null, createdAt: r.created_at,
      decidedBy: r.approved_by, decidedByName: names.get(r.approved_by) || null, decidedAt: r.approved_at,
      note: r.rejection_note || null,
    })),
  };
}

export async function resolveItemAccessRequest(spaceSlug, { requestId, decision } = {}) {
  if (!supabase || !spaceSlug) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/items/resolve-access-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ requestId, decision }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * Item Share Modal — the one-round-trip roster read (item_roster RPC, PR-1). Resolved people
 * (display name, email, is_claimed, projected role, is_creator) + groups (display name, member
 * count; UNRESOLVED slugs come back raw) + pending_request_count. Gated server-side on
 * can_edit_item — non-editors get null (quiet deny), same as any failure here.
 * @returns {Promise<null | { item_id:string, created_by:string|null, people:Array, groups:Array, pending_request_count:number }>}
 */
export async function fetchItemRoster(itemId) {
  if (!supabase || !itemId) return null;
  const { data, error } = await supabase.rpc('item_roster', { p_item: itemId });
  if (error) { console.warn('[share] item_roster failed:', error.message); return null; }
  return data || null;
}

/**
 * P4a — read access requests (RLS-gated: you see your own + any for items in spaces you administer).
 * Pass { itemId } for the per-item approver strip, { status } to filter (default pending).
 * Returns rows joined with the requester's display label.
 */
export async function fetchItemAccessRequests(spaceSlug, { itemId, status = 'pending' } = {}) {
  if (!supabase) return [];
  let q = supabase
    .from('item_access_requests')
    .select('id, item_id, account_id, capability, status, note, decided_at, created_at')
    .order('created_at', { ascending: false });
  if (itemId) q = q.eq('item_id', itemId);
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) { console.warn('[access-requests] fetch failed:', error.message); return []; }
  const rows = data || [];
  // Resolve the requester via the approver-only definer RPC — the accounts table is self-read-only,
  // so an inline join returns null for other users (the old email fallback here never fired). The RPC
  // returns name + avatar + email for requests on items we can edit, so a blank name -> email.
  const actorMap = await resolveAccessRequestActors(rows.map((r) => r.id));
  return rows.map((r) => {
    const a = actorMap[r.id] || {};
    return {
      id: r.id, itemId: r.item_id, accountId: r.account_id, capability: r.capability,
      status: r.status, note: r.note, createdAt: r.created_at, decidedAt: r.decided_at,
      requesterLabel: a.display_name || a.email || 'Someone',
      requesterAvatar: a.avatar_url || null,
    };
  });
}

/**
 * Resolve access-request requester labels (name + avatar + email) via the approver-only
 * access_request_actors RPC. Returns a map of request id -> { display_name, avatar_url, email };
 * requests the caller can't approve simply come back absent (quiet deny). Shared by the inbox bell
 * and the per-item approver strip so both fall back display_name -> email -> 'Someone' identically.
 */
async function resolveAccessRequestActors(requestIds) {
  const ids = [...new Set((requestIds || []).filter(Boolean))];
  if (!supabase || !ids.length) return {};
  const { data, error } = await supabase.rpc('access_request_actors', { p_request_ids: ids });
  if (error) { console.warn('[access-requests] actor resolve failed:', error.message); return {}; }
  return Object.fromEntries((data || []).map((a) => [a.request_id, a]));
}

/**
 * P4b — inbox: read my deliveries for a space (RLS scopes to my account rows + the group queues I'm an
 * active member of). Resolves `item_access_request` refs in a batch so each row can render + act
 * (render-don't-copy: status comes from the referenced request, never the delivery).
 */
/**
 * Of the given accounts, the ones who could not read this item — the people a mention is about to
 * point at a wall. Editor-gated server-side, so a caller who could not grant access gets an empty
 * list and never shows the offer.
 * @returns {Promise<string[]>}
 */
export async function fetchAccountsLackingItemView(itemId, accountIds) {
  if (!supabase || !itemId) return [];
  const ids = [...new Set((accountIds || []).filter(Boolean))];
  if (!ids.length) return [];
  const { data, error } = await supabase.rpc('accounts_lacking_item_view', {
    p_item: itemId, p_account_ids: ids,
  });
  if (error) { console.warn('[mentions] accounts_lacking_item_view failed:', error.message); return []; }
  return (data || []).map((r) => r.r_account_id);
}

/**
 * The narrower companion to the above: yes-or-no view flags per account, answerable by anyone who
 * can view the item themselves (not just editors). Powers the @ picker's greyed-out rows. Returns a
 * map of account id → boolean; empty when the caller can't view the item, or on any failure —
 * failure degrades to "grey nobody", never to a blocked picker.
 * @returns {Promise<Record<string, boolean>>}
 */
export async function fetchItemViewFlags(itemId, accountIds) {
  if (!supabase || !itemId) return {};
  const ids = [...new Set((accountIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await supabase.rpc('accounts_item_view_flags', {
    p_item: itemId, p_account_ids: ids,
  });
  if (error) { console.warn('[mentions] accounts_item_view_flags failed:', error.message); return {}; }
  return Object.fromEntries((data || []).map((r) => [r.r_account_id, !!r.r_can_view]));
}

/**
 * Can the signed-in account edit this item? The per-item RLS predicate, asked directly. Used to
 * decide whether the @ picker greys unreachable people (a non-editor cannot fix their access) or
 * leaves them selectable for the grant offer (an editor can). Null on failure — callers treat null
 * as "unknown", which degrades to today's ungreyed picker.
 * @returns {Promise<boolean|null>}
 */
export async function fetchCanEditItem(itemId) {
  if (!supabase || !itemId) return null;
  const { data, error } = await supabase.rpc('can_edit_item', { p_item: itemId });
  if (error) { console.warn('[mentions] can_edit_item failed:', error.message); return null; }
  return typeof data === 'boolean' ? data : null;
}

// ─── Connections — the consent edge between two accounts ───────────────────────────────────────────
// Symmetric with consent (vault: 2026-07-29 DM + connections doc, F6/F7). Every write is a definer
// RPC; the client never touches the table, so a state transition cannot be faked from the browser.

/**
 * Connection state of each listed account relative to me.
 * @returns {Promise<Record<string, 'none'|'pending_out'|'pending_in'|'connected'|'declined'|'blocked'>>}
 */
export async function fetchConnectionStates(accountIds) {
  if (!supabase) return {};
  const ids = [...new Set((accountIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await supabase.rpc('connection_states', { p_account_ids: ids });
  if (error) { console.warn('[connections] connection_states failed:', error.message); return {}; }
  return Object.fromEntries((data || []).map((r) => [r.r_account_id, r.r_state]));
}

/**
 * Ask someone to connect — or accept their outstanding ask, if they got there first.
 * @returns {Promise<string>} the caller-relative state after the call
 */
export async function requestConnection(accountId, { originSpaceId = null, message = null } = {}) {
  if (!supabase || !accountId) return 'none';
  const { data, error } = await supabase.rpc('request_connection', {
    p_other: accountId, p_origin_space: originSpaceId, p_message: message,
  });
  if (error) { console.warn('[connections] request_connection failed:', error.message); return 'error'; }
  return data || 'none';
}

/** Answer an incoming request, or block (either party, any time — also how a connection is undone). */
export async function respondToConnection(accountId, decision) {
  if (!supabase || !accountId) return 'none';
  const { data, error } = await supabase.rpc('respond_to_connection', {
    p_other: accountId, p_decision: decision,
  });
  if (error) { console.warn('[connections] respond_to_connection failed:', error.message); return 'error'; }
  return data || 'none';
}

/** Withdraw a request I sent. Leaves no trace on either side. */
export async function cancelConnectionRequest(accountId) {
  if (!supabase || !accountId) return 'none';
  const { data, error } = await supabase.rpc('cancel_connection_request', { p_other: accountId });
  if (error) { console.warn('[connections] cancel_connection_request failed:', error.message); return 'error'; }
  return data || 'none';
}

/**
 * End a connection without blocking. Either person, not announced. It takes each out of the other's
 * Connections group, so whatever either shared with their connections stops reaching the other.
 * @returns {Promise<'none'|'not-connected'|'error'|string>}
 */
export async function removeConnection(accountId) {
  if (!supabase || !accountId) return 'none';
  const { data, error } = await supabase.rpc('remove_connection', { p_other: accountId });
  if (error) { console.warn('[connections] remove_connection failed:', error.message); return 'error'; }
  return data || 'none';
}

// ─── Personal groups — audiences a person owns, starting with My Connections ──────────────────────
// A personal group lives in its owner's Personal Space and puts people in audiences only, never in the
// space (20260914000607). A row names one with the token `@personal-group:<group-id>`, view axis only.

/**
 * My personal groups, the Connections group first, with how many people are in each.
 * @returns {Promise<Array<{ id: string, slug: string, label: string, isSystem: boolean, count: number, token: string }>>}
 */
export async function fetchMyPersonalGroups() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('my_personal_groups');
  if (error) { console.warn('[groups] my_personal_groups failed:', error.message); return []; }
  return (data || []).map((r) => ({
    id: r.r_group_id,
    slug: r.r_slug,
    label: r.r_is_system && r.r_slug === 'connections' ? 'My Connections' : r.r_display_name,
    isSystem: r.r_is_system,
    count: r.r_account_count,
    token: r.r_token,
  }));
}

/**
 * What the personal-group tokens on a row are, for someone reading its audience. A group's own name
 * comes back only for the reader's own groups; anyone else's is described by its owner.
 * @param {string[]} groupIds
 * @returns {Promise<Record<string, { isConnections: boolean, name: string|null, ownerId: string, ownerName: string|null, isMine: boolean }>>}
 */
export async function describePersonalGroups(groupIds) {
  const ids = [...new Set((groupIds || []).filter(Boolean))];
  if (!supabase || !ids.length) return {};
  const { data, error } = await supabase.rpc('describe_personal_groups', { p_group_ids: ids });
  if (error) { console.warn('[groups] describe_personal_groups failed:', error.message); return {}; }
  return Object.fromEntries((data || []).map((r) => [r.r_group_id, {
    isConnections: r.r_is_connections,
    name: r.r_display_name,
    ownerId: r.r_owner_account,
    ownerName: r.r_owner_name,
    isMine: r.r_is_mine,
  }]));
}

/**
 * Who is in one of my personal groups (migration 20261124090000): names and photos, never emails.
 * @returns {Promise<Array<{ id: string, label: string, avatar: string|undefined }>>}
 */
export async function fetchPersonalGroupPeople(groupId) {
  if (!supabase || !groupId) return [];
  const { data, error } = await supabase.rpc('personal_group_people', { p_group: groupId });
  if (error) throw new Error(error.message);
  return (data || []).map((r) => ({ id: r.r_account_id, label: r.r_display_name || 'Someone', avatar: r.r_avatar_url || undefined }));
}

/** Make a personal group. Throws with the database's reason (no name, too many groups). */
export async function createPersonalGroup(name) {
  const { data, error } = await supabase.rpc('create_personal_group', { p_name: name });
  if (error) throw new Error(error.message);
  return data;
}

export async function renamePersonalGroup(groupId, name) {
  const { error } = await supabase.rpc('rename_personal_group', { p_group: groupId, p_name: name });
  if (error) throw new Error(error.message);
}

/** Archive and empty a group, so every row shared with it stops reaching anyone. */
export async function deletePersonalGroup(groupId) {
  const { error } = await supabase.rpc('delete_personal_group', { p_group: groupId });
  if (error) throw new Error(error.message);
}

/** Add connections to one of my groups; anyone who is not a connection is skipped. @returns {Promise<number>} */
export async function addToPersonalGroup(groupId, accountIds) {
  const { data, error } = await supabase.rpc('add_to_personal_group', { p_group: groupId, p_accounts: accountIds });
  if (error) throw new Error(error.message);
  return data || 0;
}

export async function removeFromPersonalGroup(groupId, accountId) {
  const { error } = await supabase.rpc('remove_from_personal_group', { p_group: groupId, p_account: accountId });
  if (error) throw new Error(error.message);
}

/** Delivery types whose rows resolve through `mention_delivery_refs` rather than an inline join. */
const MENTION_TYPES = new Set(['note_mention', 'page_mention']);

export async function fetchInboxDeliveries(spaceSlug) {
  if (!supabase) return [];
  let spaceId = null;
  if (spaceSlug) {
    const { data } = await supabase.rpc('space_id_by_slug', { p_slug: spaceSlug });
    spaceId = data || null;
  }
  let q = supabase
    .from('deliveries')
    // Newest by when each LAST FIRED: a chat keeps one row per recipient and re-arms it on every new
    // message, so created_at put today's message at the day the chat began (20261110153106).
    .select('id, notification_type, recipient_type, ref_type, ref_id, state, message, created_at, fired_at, primary_space_id')
    .order('fired_at', { ascending: false }).limit(100);
  if (spaceId) q = q.eq('primary_space_id', spaceId);
  const { data: rows, error } = await q;
  if (error) { console.warn('[inbox] deliveries fetch failed:', error.message); return []; }
  const deliveries = rows || [];

  // Each row resolves its own space slug. The list is cross-space by default, but the workspace API
  // routes (approve/deny an access request) are per-space — so an actionable row must know which
  // space it belongs to, not which space the panel happens to be scoped to.
  const rowSpaceIds = [...new Set(deliveries.map((d) => d.primary_space_id).filter(Boolean))];
  let spaceSlugMap = {};
  if (rowSpaceIds.length) {
    const { data: rowSpaces } = await supabase.from('spaces').select('id, slug').in('id', rowSpaceIds);
    spaceSlugMap = Object.fromEntries((rowSpaces || []).map((s) => [s.id, s.slug]));
  }

  const reqIds = [...new Set(deliveries.filter((d) => d.ref_type === 'item_access_request').map((d) => d.ref_id))];
  let reqMap = {};
  if (reqIds.length) {
    const { data: reqs } = await supabase
      .from('item_access_requests')
      .select('id, item_id, account_id, capability, status, items:item_id(title)')
      .in('id', reqIds);
    // Resolve requester name/avatar/email via the approver-only definer RPC — the accounts table is
    // self-read-only, so an inline join returns null for other users. A blank display_name falls back
    // to email (we're the approver) so the row never reads a bare "Someone wants to view …".
    const actorMap = await resolveAccessRequestActors((reqs || []).map((r) => r.id));
    reqMap = Object.fromEntries((reqs || []).map((r) => {
      const a = actorMap[r.id] || {};
      return [r.id, {
        id: r.id, itemId: r.item_id, capability: r.capability, status: r.status,
        itemTitle: r.items?.title || null,
        requesterId: r.account_id || null,
        requesterLabel: a.display_name || a.email || 'Someone',
        requesterAvatar: a.avatar_url || null,
      }];
    }));
  }

  // Bare item refs (ref_type 'item' — e.g. vault_sync_conflict) resolve straight to the item, no
  // request in between. Same render-don't-copy rule: the title comes from the row NOW, so a renamed
  // or deleted item reads correctly instead of replaying a stale copy taken at emit time.
  const itemIds = [...new Set(deliveries.filter((d) => d.ref_type === 'item').map((d) => d.ref_id))];
  let itemMap = {};
  if (itemIds.length) {
    // approval_status lets a settled approval notice say what was decided, not only that it was.
    const { data: items } = await supabase.from('items').select('id, title, approval_status').in('id', itemIds);
    itemMap = Object.fromEntries((items || []).map((r) => [r.id, {
      id: r.id, title: r.title || null, approvalStatus: r.approval_status || null,
    }]));
  }

  // Mention rows resolve through their own definer RPC: the point of being told you were mentioned is
  // that you can read it BEFORE you have rights on the note, which no client-side join could do. It
  // returns the item to open plus the actor and a snippet, all read from the source rows (P2).
  // Connection rows resolve to the OTHER party plus the edge's current state, so a request already
  // accepted renders as accepted instead of replaying the ask.
  const connectionRows = deliveries.filter((d) => d.ref_type === 'connection');
  let connectionMap = {};
  if (connectionRows.length) {
    const { data: refs, error: connErr } = await supabase
      .rpc('connection_delivery_refs', { p_delivery_ids: connectionRows.map((d) => d.id) });
    if (connErr) console.warn('[inbox] connection_delivery_refs failed:', connErr.message);
    connectionMap = Object.fromEntries((refs || []).map((r) => [r.r_delivery_id, {
      accountId: r.r_account_id,
      actorLabel: r.r_actor_label || 'Someone',
      actorAvatar: r.r_actor_avatar || null,
      state: r.r_state || 'none',
    }]));
  }

  // Not just mentions: any account-addressed row whose ref is a note (reminders, assignments)
  // resolves through the same definer RPC — it keys on ref_type + recipient, not the notification
  // type, and without it a note_reminder renders its {{item}} as a bare "an item".
  const mentionRows = deliveries.filter(
    (d) => MENTION_TYPES.has(d.notification_type)
      || (d.ref_type === 'note' && d.recipient_type === 'account'),
  );
  let mentionMap = {};
  if (mentionRows.length) {
    const { data: refs, error: refErr } = await supabase
      .rpc('mention_delivery_refs', { p_delivery_ids: mentionRows.map((d) => d.id) });
    if (refErr) console.warn('[inbox] mention_delivery_refs failed:', refErr.message);
    // The resolver's OUT columns are r_-prefixed (a SQL function's RETURNS TABLE names are in scope
    // inside its own body, so `item_id` would have collided with notes.item_id) — read them as named.
    mentionMap = Object.fromEntries((refs || []).map((r) => [r.r_delivery_id, {
      itemId: r.r_item_id || null,
      itemTitle: r.r_item_title || null,
      // Null for a name-hidden author ON PURPOSE — the resolver masks the id with the label and
      // avatar, so the popup's avatar stays unclickable exactly where the name stays hidden.
      actorId: r.r_actor_id || null,
      actorLabel: r.r_actor_label || 'Someone',
      actorAvatar: r.r_actor_avatar || null,
      snippet: r.r_snippet || null,
    }]));
  }

  // Chat rows — a direct message, a group message, a group-chat invite — point at the CONVERSATION.
  // Without this lookup they had nothing to name: "Someone sent you a message", "Someone wrote in an
  // item", and no click (2026-10-09). The resolver answers who to name, the chat's title (the other
  // person, for a DM), where it is filed, and the newest message's opening.
  const chatRows = deliveries.filter((d) => d.ref_type === 'conversation');
  let chatMap = {};
  if (chatRows.length) {
    const { data: refs, error: chatErr } = await supabase
      .rpc('conversation_delivery_refs', { p_delivery_ids: chatRows.map((d) => d.id) });
    if (chatErr) console.warn('[inbox] conversation_delivery_refs failed:', chatErr.message);
    chatMap = Object.fromEntries((refs || []).map((r) => [r.r_delivery_id, {
      id: r.r_conversation_id,
      kind: r.r_kind || 'group',
      title: r.r_chat_title || null,
      spaceSlug: r.r_space_slug || null,
      actorId: r.r_account_id || null,
      actorLabel: r.r_actor_label || 'Someone',
      actorAvatar: r.r_actor_avatar || null,
      snippet: r.r_snippet || null,
      // For a mention in a chat: the message that named the reader, so the click lands on it.
      messageId: r.r_message_id || null,
    }]));
  }

  return deliveries.map((d) => {
    const mention = mentionMap[d.id] || null;
    const connection = connectionMap[d.id] || null;
    return {
      connection,
      chat: chatMap[d.id] || null,
      id: d.id, type: d.notification_type, recipientType: d.recipient_type,
      refType: d.ref_type, refId: d.ref_id, state: d.state, message: d.message, createdAt: d.fired_at || d.created_at,
      spaceId: d.primary_space_id || null, spaceSlug: spaceSlugMap[d.primary_space_id] || null,
      request: d.ref_type === 'item_access_request' ? (reqMap[d.ref_id] || null) : null,
      // A mention's item comes from the mention resolver when it has one, so a note mention still
      // knows which item to open even though its ref points at the note.
      item: mention?.itemId
        ? { id: mention.itemId, title: mention.itemTitle }
        : (d.ref_type === 'item' ? (itemMap[d.ref_id] || null) : null),
      mention,
    };
  });
}

/**
 * The people the signed-in account may @mention in this space: its active members plus the accounts
 * they are connected to (see `mentionable_account_ids`). Name + avatar only — the RPC never returns
 * email or phone, same exposure rule as the privacy picker's people pool.
 */
/**
 * The active space's uuid from its slug.
 *
 * `getActiveSpaceId()` is NOT it: `setBackendConfig` only carries an id when whatever flipped the
 * space happened to know one, and on an ordinary page load it is null while the slug is set. A hook
 * keyed on the id therefore never fires — which is exactly how the group cards first shipped
 * empty, with no error anywhere, because "no space id" and "no groups" look the same downstream.
 *
 * `space_id_by_slug` is the same RPC `SupabaseDataService.resolveSpaceId` uses; this keeps its own
 * one-slug cache rather than reaching into that module's private one.
 */
let _spaceIdBySlug = null; let _spaceIdBySlugKey = null;
export async function spaceIdForSlug(slug) {
  if (!supabase || !slug) return null;
  if (_spaceIdBySlugKey === slug && _spaceIdBySlug) return _spaceIdBySlug;
  const { data, error } = await supabase.rpc('space_id_by_slug', { p_slug: slug });
  if (error) { console.warn('[groups] space_id_by_slug failed:', error.message); return null; }
  _spaceIdBySlugKey = slug; _spaceIdBySlug = data || null;
  return _spaceIdBySlug;
}

/**
 * A space's `config` blob, for the one key the placement switch reads.
 *
 * `spaces_read` (20260623120000) already lets an active member SELECT their own space, so this
 * exposes nothing new. Returns null on any failure, which the caller treats as "not scoped" — the
 * safe direction, because an unscoped space falls back to stores that are still there while an
 * empty scoped state would silently re-lay-out every tab.
 */
export async function fetchSpaceConfig(spaceSlug) {
  const spaceId = await spaceIdForSlug(spaceSlug);
  if (!supabase || !spaceId) return null;
  const { data, error } = await supabase.from('spaces').select('config').eq('id', spaceId).maybeSingle();
  if (error) { console.warn('[placements] fetchSpaceConfig failed:', error.message); return null; }
  return data?.config || null;
}

/**
 * Every placement row this space resolves against: its own, plus the LIBRARY rows.
 *
 * Library rows come too because a canonical type's per-field layout is stored as one
 * (`primary_space_id IS NULL`), and the resolver needs both in hand to let a space row beat a
 * library row at the same scope. `field_placements_read` is world-readable, so this needs no
 * membership of its own.
 */
export async function fetchPlacementRows(spaceSlug) {
  const spaceId = await spaceIdForSlug(spaceSlug);
  if (!supabase || !spaceId) return [];
  const { data, error } = await supabase
    .from('field_placements')
    .select('id, field_id, primary_space_id, scope_kind, scope_id, info_rung, edit_rung')
    .or(`primary_space_id.eq.${spaceId},primary_space_id.is.null`);
  if (error) { console.warn('[placements] fetchPlacementRows failed:', error.message); return []; }
  return data || [];
}

/**
 * The space's groups, for anywhere a group token has to be rendered as itself.
 *
 * A plain table read rather than an RPC: `groups_read` (migration 20260623120000) already lets any
 * active member SELECT the groups of a space they belong to, and space-less system groups are
 * globally readable. So there is nothing here a member could not already see.
 *
 * Returns [] rather than throwing on any failure, because every caller renders a group with or
 * without its metadata — a missing colour costs a dot, not a screen.
 *
 * @param {string} spaceSlug
 * @returns {Promise<Array<{id,slug,display_name,description,color,icon_url}>>}
 */
export async function fetchSpaceGroups(spaceSlug) {
  const spaceId = await spaceIdForSlug(spaceSlug);
  if (!supabase || !spaceId) return [];
  const { data, error } = await supabase
    .from('groups')
    .select('id, slug, display_name, description, color, icon_url, is_system, lifecycle')
    .eq('primary_space_id', spaceId);
  if (error) { console.warn('[groups] fetchSpaceGroups failed:', error.message); return []; }
  return (data || []).filter((g) => !isLinkGroupSlug(g.slug));
}

/**
 * Who is in each group of a space, as `{ group_id, account_id }` rows.
 *
 * One read for every group rather than one per card: a space has a handful of groups and the join
 * is cheap, while opening a card per group would be a round-trip each time somebody hovers an
 * audience. `gm_space_read` (migration 20260623120000) already lets an active member SELECT this
 * for their own space.
 *
 * Only `active` rows. An invited-but-not-joined account is not in the group yet, and counting them
 * would make a card claim access that RLS does not actually grant.
 *
 * @param {string} spaceSlug
 * @returns {Promise<Array<{group_id: string, account_id: string}>>}
 */
export async function fetchSpaceGroupMembers(spaceSlug) {
  const spaceId = await spaceIdForSlug(spaceSlug);
  if (!supabase || !spaceId) return [];
  const { data, error } = await supabase
    .from('group_members')
    .select('group_id, account_id')
    .eq('primary_space_id', spaceId)
    .eq('status', 'active');
  if (error) { console.warn('[groups] fetchSpaceGroupMembers failed:', error.message); return []; }
  return data || [];
}

/**
 * Which profile item each member of a space is, as `{ [accountId]: profileItemId }` — the pointer an
 * answer's author is folded through so a person who writes and a person written about are one node.
 * Same read and the same `gm_space_read` policy as fetchSpaceGroupMembers; an account seated in
 * several groups carries the same pointer in each, so the first non-null one wins.
 *
 * @param {string} spaceSlug
 * @returns {Promise<Object<string, string>>}
 */
export async function fetchSpaceProfilePointers(spaceSlug) {
  const spaceId = await spaceIdForSlug(spaceSlug);
  if (!supabase || !spaceId) return {};
  const { data, error } = await supabase
    .from('group_members')
    .select('account_id, profile_item_id')
    .eq('primary_space_id', spaceId)
    .eq('status', 'active')
    .not('profile_item_id', 'is', null);
  if (error) { console.warn('[groups] fetchSpaceProfilePointers failed:', error.message); return {}; }
  const out = {};
  for (const r of data || []) if (r.account_id && !out[r.account_id]) out[r.account_id] = r.profile_item_id;
  return out;
}

export async function fetchMentionableAccounts(spaceSlug) {
  if (!supabase || !spaceSlug) return [];
  const { data, error } = await supabase.rpc('mentionable_accounts', { p_space_slug: spaceSlug });
  if (error) { console.warn('[mentions] mentionable_accounts failed:', error.message); return []; }
  return (data || []).map((r) => ({
    id: r.id,
    displayName: r.display_name || '',
    avatarUrl: r.avatar_url || null,
    inSpace: !!r.in_space,
    isConnection: !!r.is_connection,
  }));
}

/**
 * Announce a mention to the accounts it names. `deliveries` has no client INSERT policy, so this RPC
 * is the only door — and it re-derives the actor, the space, the ref and every recipient server-side,
 * which is why passing an account the caller may not mention is a no-op rather than an error.
 *
 * Best-effort by design: a mention that fails to notify must never fail the save that carried it.
 * @param {string} spaceSlug
 * @param {{refType: 'note'|'item', refId: string, accountIds: string[]}} target
 * @returns {Promise<number>} how many deliveries were written (0 when already announced)
 */
export async function emitMentionDeliveries(spaceSlug, { refType, refId, accountIds } = {}) {
  if (!supabase || !spaceSlug || !refId) return 0;
  const ids = [...new Set((accountIds || []).filter(Boolean))];
  if (!ids.length) return 0;
  const { data, error } = await supabase.rpc('emit_mention_deliveries', {
    p_space_slug: spaceSlug, p_ref_type: refType, p_ref_id: refId, p_account_ids: ids,
  });
  if (error) { console.warn('[mentions] emit_mention_deliveries failed:', error.message); return 0; }
  return data || 0;
}

/** P4b — set the read-state on one or more of my deliveries (seen on open, done on action/dismiss). */
export async function markDeliveryState(ids, state) {
  if (!supabase || !ids || !ids.length) return;
  let q = supabase
    .from('deliveries').update({ state, updated_at: new Date().toISOString() }).in('id', ids);
  // "Seen" only ever moves a row on from unseen. The list it is computed from can be stale: an admin
  // who decided a share or an approval elsewhere has had the server mark that row done, and opening
  // the bell wrote it back to seen, bringing back buttons for something already decided.
  if (state === 'seen') q = q.eq('state', 'unseen');
  const { error } = await q;
  if (error) console.warn('[inbox] markDeliveryState failed:', error.message);
}

/**
 * The notification-type registry (public read RLS). Powers the bell's type dropdown + per-row
 * template/click-action rendering, and the /brand Notifications tab. Ordered by sort_order.
 */
export async function fetchNotificationTypes() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('notification_types')
    .select('key, label, description, category, recipient_kind, default_on_channels, required_channels, is_active, sort_order, title_template, body_template, click_action')
    .order('sort_order', { ascending: true });
  if (error) { console.warn('[notif] fetchNotificationTypes failed:', error.message); return []; }
  return data || [];
}

/**
 * Build → Notifications: how much each type is used, for every row rather than only the caller's
 * (notification_type_stats is platform-admin gated and answers empty for anyone else). Keyed by type.
 */
export async function fetchNotificationTypeStats() {
  if (!supabase) return {};
  const { data, error } = await supabase.rpc('notification_type_stats');
  if (error) { console.warn('[notif] notification_type_stats failed:', error.message); return {}; }
  return Object.fromEntries((data || []).map((r) => [r.r_type, {
    total: Number(r.r_total) || 0, last30d: Number(r.r_last_30d) || 0, unseen: Number(r.r_unseen) || 0,
    lastFiredAt: r.r_last_fired_at || null, emailsTotal: Number(r.r_emails_total) || 0, emails30d: Number(r.r_emails_30d) || 0,
  }]));
}

/**
 * Edit-key → admin enrollment (P2). A signed-in (claimed) user who knows the workspace password
 * promotes their own account to admin. Server validates the code + adds them to the admin group.
 * @returns {Promise<{ ok:boolean, promoted?:boolean, error?:string }>}
 */
export async function claimAdminByCode(spaceSlug, code) {
  if (!supabase || !spaceSlug) return { ok: false, error: 'not-configured' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'not-authenticated' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/claim-admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ password: code }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

// ─── PROF-1: profile item RPCs ────────────────────────────────────────────────────────────────────
/**
 * Fetch the current user's profile_item_id for a space. Returns the UUID or null.
 *
 * A TRASHED profile does not count. Nothing clears `profile_item_id` when the item it names is
 * soft-deleted — not the trash action, not `admin_set_profile_item` — so a membership can outlive
 * its profile and go on resolving to a row in the bin. EarthKind carried eight of those on
 * 2026-09-08, two of them real members whose profiles had been tidied away in an August cleanup:
 * every viewer-anchored surface rendered against a deleted item instead of reporting no profile.
 * `null` is the honest answer there, and it is the one the callers already handle — signed in
 * without a claimed profile is a normal, designed-for state (see useEventViewer).
 *
 * The order sync's own profile-link rung has always taken this care (`_arcos-supabase.js` dedup 0:
 * "the linked item must be live and not a collection"); the read path simply never matched it.
 */
/**
 * Is there a profile in this space carrying my email, that I could claim instead of creating one?
 * See api/workspace/[slug]/_profileMatch.js for the rules and the statuses. Resolves to
 * `{ status: 'unknown' }` on any failure, which callers treat as "no match" — a failed lookup must
 * never stop someone setting up a profile.
 */
export async function fetchProfileMatch(spaceSlug) {
  if (!supabase || !spaceSlug) return { status: 'unknown' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { status: 'unknown' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/profile-match`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { status: 'unknown' };
    return await res.json();
  } catch {
    return { status: 'unknown' };
  }
}

/** Claim the profile `fetchProfileMatch` found. Resolves to the endpoint's `{ status, person? }`. */
export async function claimProfileMatch(spaceSlug, personItemId) {
  if (!supabase || !spaceSlug || !personItemId) return { status: 'unknown' };
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { status: 'unknown' };
  try {
    const res = await fetch(`/api/workspace/${encodeURIComponent(spaceSlug)}/profile-match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ personItemId }),
    });
    return await res.json().catch(() => ({ status: 'unknown' }));
  } catch {
    return { status: 'unknown' };
  }
}

export async function fetchMyProfileItemId(spaceSlug) {
  if (!supabase || !spaceSlug) return null;
  const uid = await getCurrentAccountId();
  if (!uid) return null;
  return sharedRead(`profileItem|${uid}|${spaceSlug}`, () => readMyProfileItemId(spaceSlug, uid));
}

async function readMyProfileItemId(spaceSlug, uid) {
  const { data, error } = await supabase
    .from('group_members')
    .select('profile_item_id, spaces!inner(slug), items!inner(is_deleted)')
    .eq('account_id', uid)
    .eq('status', 'active')
    .eq('spaces.slug', spaceSlug)
    .eq('items.is_deleted', false)
    .not('profile_item_id', 'is', null)
    .limit(1)
    .maybeSingle();
  if (error) { console.warn('[prof] fetchMyProfileItemId failed:', error.message); return null; }
  return data?.profile_item_id ?? null;
}

/**
 * Link a profile item to the caller's membership in a space.
 * @returns {Promise<string>} 'ok' | 'no-space' | 'item-not-found' | 'not-a-member' | ...
 */
export async function setProfileItemId(spaceSlug, itemId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('set_profile_item_id',
    { p_space_slug: spaceSlug, p_item_id: itemId });
  if (error) { console.warn('[prof] setProfileItemId failed:', error.message); return null; }
  return data;
}

/**
 * Resolve any active member's profile_item_id in a space.
 * @returns {Promise<string|null>} item UUID or null
 */
export async function fetchMemberProfileItemId(spaceSlug, accountId) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('member_profile_item',
    { p_space_slug: spaceSlug, p_account_id: accountId });
  if (error) { console.warn('[prof] fetchMemberProfileItemId failed:', error.message); return null; }
  return data;
}

// ─── ACCT-1: account settings RPCs ─────────────────────────────────────────────────────────────────

/** Soft-delete the signed-in account (sets deleted_at, deactivates memberships). */
export async function softDeleteAccount() {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('soft_delete_account');
  if (error) { console.warn('[acct] soft_delete_account failed:', error.message); return null; }
  return data;
}

/** Reclaim a soft-deleted account within the 30-day window. */
export async function reclaimAccount() {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('reclaim_account');
  if (error) { console.warn('[acct] reclaim_account failed:', error.message); return null; }
  return data;
}

// ─── ONB-2: claim intent status check ─────────────────────────────────────────────────────────────
/**
 * Check the status of a claim token without redeeming it. Used to distinguish expired/consumed
 * tokens from wrong-account tokens when a ct redeem fails.
 * @returns {Promise<'valid'|'expired'|'consumed'|'not-found'|null>}
 */
export async function checkClaimIntentStatus(token) {
  if (!supabase || !token) return null;
  const { data, error } = await supabase.rpc('check_claim_intent_status', { p_token: token });
  if (error) { console.warn('[onb] checkClaimIntentStatus failed:', error.message); return null; }
  return data;
}

/**
 * Everything the recovery landing needs about a claim token, WITHOUT a session — status, the space
 * it targets, who invited them, when the invitation lapses, and the masked recipient address.
 *
 * The pre-auth part is the point. checkClaimIntentStatus above was only ever called AFTER a failed
 * redeem, which needs a session — so when the magic link itself died there was nobody to ask on
 * behalf of, and the app fell back to "not a member" five seconds later. This runs on the anon key
 * (the RPC is SECURITY DEFINER, granted to anon), so the landing can tell "your invitation is still
 * good" from "it expired" BEFORE asking anyone to sign in.
 *
 * @returns {Promise<{status:string, kind:string|null, spaceSlug:string|null, spaceName:string|null,
 *                    inviterName:string|null, expiresAt:string|null, maskedEmail:string|null}|null>}
 */
export async function fetchClaimIntentLanding(token) {
  if (!supabase || !token) return null;
  const { data, error } = await supabase.rpc('claim_intent_landing', { p_token: token });
  if (error) { console.warn('[claim] claim_intent_landing failed:', error.message); return null; }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    status: row.status,
    kind: row.kind || null,
    spaceSlug: row.space_slug || null,
    spaceName: row.space_name || null,
    inviterName: row.inviter_name || null,
    expiresAt: row.expires_at || null,
    maskedEmail: row.masked_email || null,
  };
}

/**
 * Ask the server to email a fresh 6-digit sign-in code for this claim token. The recipient address
 * is resolved server-side from the token and comes back MASKED — the browser never sees it, and the
 * person is never asked to type an address they might get wrong (which would mint a stray account
 * and still leave them stuck).
 */
export async function resendClaimCode(ct) {
  if (!ct) return { ok: false, error: 'no-token' };
  try {
    const res = await fetch('/api/claim/resend-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ct }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * Exchange a 6-digit code for a session and apply it. Verification happens SERVER-side because
 * verifyOtp needs the email address, which is exactly what we are keeping out of the browser.
 * On success the session is installed here, which fires SIGNED_IN — and WorkspaceRouter's existing
 * ?ct= effect redeems the invitation off that event, so no new redemption path exists to maintain.
 */
export async function verifyClaimCode(ct, code) {
  if (!ct || !code) return { ok: false, error: 'bad-code' };
  try {
    const res = await fetch('/api/claim/verify-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ct, code }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    const { error } = await supabase.auth.setSession({
      access_token: body.access_token,
      refresh_token: body.refresh_token,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

/**
 * The ticket-claim variant of verifyClaimCode ("is this you?" on a registration form). Two
 * differences, both decided: the server marks the claim's match record proven (the order rides
 * that proof, not the session), and session tokens come back CONDITIONALLY — only for an
 * email-kind match when the caller didn't ask for proofOnly. So unlike verifyClaimCode, absent
 * tokens are a normal outcome here, and a setSession failure downgrades to a warning: the proof
 * already stands, and sign-in was the bonus, not the point.
 */
export async function verifyTicketClaimCode(ct, code, { proofOnly = false } = {}) {
  if (!ct || !code) return { ok: false, error: 'bad-code' };
  try {
    const res = await fetch('/api/claim/verify-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ct, code, proofOnly }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
    if (!proofOnly && body.access_token && body.refresh_token) {
      const { error } = await supabase.auth.setSession({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
      });
      if (error) console.warn('[verifyTicketClaimCode] setSession failed (the proof still stands):', error.message);
    }
    return {
      ok: true,
      proven: body.proven === true,
      signedIn: !proofOnly && !!body.access_token,
      // The matched profile's current values (only returned after proof) — feeds the merge view.
      fields: Array.isArray(body.fields) ? body.fields : null,
    };
  } catch (err) {
    return { ok: false, error: err.message || 'request-failed' };
  }
}

// ─── ONB-5: space door info ───────────────────────────────────────────────────────────────────────
/**
 * Public door state for a space by slug — door_discovery, door_entry, space_name. Usable by
 * anonymous visitors (the RPC is SECURITY DEFINER, granted to anon + authenticated).
 * @returns {Promise<{ doorDiscovery: string, doorEntry: string, spaceName: string } | null>}
 */
// An answered error still returns null, which the surface rule reads as fail-open. A request that never
// reached the server throws instead, marked `unreachable`, because a fail-open on THAT draws the whole
// space with nothing in it (surfaceRule.js, 'unreachable').
export async function spaceDoorInfo(slug) {
  if (!supabase || !slug) return null;
  const { data, error, status } = await supabase.rpc('space_door_info', { p_slug: slug });
  if (error) {
    console.warn('[onb] spaceDoorInfo failed:', error.message);
    if (isNetworkFailure({ status, message: error.message })) {
      throw Object.assign(new Error(`Could not reach the server: ${error.message}`), { unreachable: true, status });
    }
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return { doorDiscovery: row.door_discovery, doorEntry: row.door_entry, spaceName: row.space_name };
}

// ─── R4-1: item-share consent gate ────────────────────────────────────────────────────────────────
/**
 * Preview a pending item-share claim intent by its ?ct= token, BEFORE any session exists —
 * the consent gate renders from this (item title, space, sharer, masked recipient email).
 * anon-callable SECURITY DEFINER; the token is the capability. Returns null on error.
 * @returns {Promise<{ status:'ok'|'expired'|'consumed'|'invalid'|'unsupported',
 *   itemTitle:string|null, spaceName:string|null, sharerName:string|null,
 *   maskedEmail:string|null, axis:'viewer'|'editor'|null } | null>}
 */
export async function previewClaimIntent(token) {
  if (!supabase || !token) return null;
  const { data, error } = await supabase.rpc('claim_intent_preview', { p_token: token });
  if (error) { console.warn('[consent-gate] preview failed:', error.message); return null; }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    status: row.status,
    itemTitle: row.item_title ?? null,
    spaceName: row.space_name ?? null,
    sharerName: row.sharer_name ?? null,
    maskedEmail: row.masked_email ?? null,
    axis: row.axis ?? null,
  };
}

export default supabase;

// ─── Usage (page_events, migration 20261029120000) ──────────────────────────────────────────────
// Totals only: neither call can return a person, and any figure fewer than three people make up comes
// back as { suppressed: true }. A space's admins read their space; Arcos admins read all of Arcos.

/** A space's visit totals for its admins: { totals, by_day, by_surface, by_item } or { error }. */
export async function fetchSpaceUsage(spaceSlug, days = 30) {
  if (!supabase) return { error: 'no-client' };
  const { data, error } = await supabase.rpc('space_usage', { p_space_slug: spaceSlug, p_days: days });
  if (error) return { error: error.code === 'PGRST202' ? 'not-deployed' : error.message };
  return data || { error: 'empty' };
}

/** Visit totals across Arcos for Arcos admins: { totals, by_day, by_surface, by_space } or { error }. */
export async function fetchPlatformUsage(days = 30) {
  if (!supabase) return { error: 'no-client' };
  const { data, error } = await supabase.rpc('platform_usage', { p_days: days });
  if (error) return { error: error.code === 'PGRST202' ? 'not-deployed' : error.message };
  return data || { error: 'empty' };
}
